import { useState, useEffect, useRef } from "react";
import {
  Shield, Key, AlertTriangle, Mail, Code, Copy, Check, Network, Table2,
} from "lucide-react";

// ============================================================================
// Documentación PÚBLICA de las DOS APIs de CentralSalud — ruta abierta del
// frontend. Son dos APIs distintas que comparten host y credencial:
//   API 1 · Stock  /api/v1/stock/*  → 7 endpoints, scope = 1 cliente, escribe,
//                                     exige gestion_stock activa, sin paginar.
//   API 2 · Datos  /api/v1/bi/*     → 10 datasets, scope = red (matriz+hijas),
//                                     solo lectura, keyset por cursor. 4 de
//                                     ellos (inventario, lotes, movimientos y
//                                     catalogo) exigen gestion_stock activa en
//                                     el CLIENTE DEL TOKEN: si no, 403
//                                     stock_disabled.
// NO se documenta aquí /api/citas/publico/* (calendario para iframe): usa otro
// esquema de token y no forma parte de la oferta de integración por Bearer.
//
// App.js declara /stock-api-docs SIN autenticación a propósito: los integradores
// del cliente tienen que poder leerla sin cuenta CentralSalud. No se enlaza
// desde menús; se accede por URL directa o desde el panel "API Keys".
// El estilo replica el branding de CentralSalud (verde #3cbf9a) y mantiene
// la estructura de sidebar fijo + secciones que tenía la versión HTML.
//
// FUENTE DE VERDAD del comportamiento: CentralSaludBackend/routes/StockApi.js
// Si modificas ese fichero, revisa esta documentación.
// ============================================================================

const PRIMARY = "#3cbf9a";
const PRIMARY_DARK = "#2da080";
const PRIMARY_LIGHT = "rgba(60, 191, 154, 0.08)";
const BASE_URL = "https://centralsaludbackend-production.up.railway.app";

// ─── Componente principal ──────────────────────────────────────────────────
/**
 * Documentación de las APIs.
 * @param {'stock'|'datos'} [api] Si se indica, muestra SOLO esa API (el tronco
 *   común —autenticación, token, scopes, seguridad, errores— se mantiene en
 *   ambas vistas). Sin prop, muestra las dos (documento completo).
 *   Rutas: /api-docs/stock · /api-docs/datos · /stock-api-docs (completo)
 */
export default function StockApiDocs({ api }) {
  const verStock = api !== 'datos';
  const verBi    = api !== 'stock';
  const [activeId, setActiveId] = useState("introduccion");
  const observerRef = useRef(null);

  // Resalta en el sidebar la sección visible al hacer scroll.
  useEffect(() => {
    const sections = document.querySelectorAll("section[id]");
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActiveId(e.target.id);
        });
      },
      { rootMargin: "-40% 0px -55% 0px" }
    );
    sections.forEach((s) => observerRef.current.observe(s));
    return () => observerRef.current?.disconnect();
  }, []);

  return (
    <div style={styles.page}>
      <Sidebar activeId={activeId} api={api} />
      <div style={styles.mainContainer}>
        <main style={styles.main}>
          <Hero />
          <RequisitoPrevio />

          <Section id="introduccion" title="Introducción">
            <p style={styles.p}>
              CentralSalud publica <strong>dos APIs REST distintas</strong> bajo el mismo host y con
              la misma credencial. No son dos grupos de endpoints de lo mismo: tienen propósito,
              alcance de datos, paginación y formato de respuesta diferentes.
            </p>
            <Table
              headers={["API", "Prefijo", "Para qué sirve"]}
              rows={[
                [
                  "1 · API de Stock",
                  "/api/v1/stock/*",
                  "Operar el inventario de UNA clínica: registrar entradas y salidas y consultar stock, lotes, historial y catálogo. Es la única que escribe.",
                ],
                [
                  "2 · API de Datos (BI)",
                  "/api/v1/bi/*",
                  "Extraer 10 datasets tabulares (pedidos, facturación, stock y dimensiones) de TODA la red para Power BI, Excel, Looker o un data warehouse. Solo lectura. Cuatro de ellos requieren el servicio de gestión de stock contratado.",
                ],
              ]}
            />
            <p style={styles.p}>
              Ambas aceptan JSON, devuelven JSON y se autentican con el{" "}
              <strong>mismo token Bearer</strong> emitido por CentralSalud. Cada endpoint incluye
              ejemplos en <strong>curl, Node.js, Python y PHP</strong> listos para copiar.
            </p>
            <p style={styles.p}>
              <strong>Base URL (común a las dos):</strong>{" "}
              <code style={styles.codeInline}>{BASE_URL}</code>
            </p>
            <Callout type="warning" icon={<AlertTriangle size={16} />}>
              <strong>Compartir credencial no significa compartir alcance.</strong> El mismo{" "}
              <code style={styles.codeInline}>csk_live_</code> ve un único cliente en la API de
              Stock y toda la red (matriz más clínicas hijas) en la API de Datos. Antes de integrar,
              lee la <a href="#comparativa" style={styles.link}>comparativa</a> y{" "}
              <a href="#scopes" style={styles.link}>Alcance y scopes</a>.
            </Callout>
          </Section>

          <Section id="comparativa" title="Las dos APIs, comparadas">
            <p style={styles.p}>
              Tabla de decisión rápida. Cada fila que resulte sorprendente está desarrollada en una
              sección posterior.
            </p>
            <Table
              headers={["Criterio", "API de Stock", "API de Datos (BI)"]}
              rows={[
                ["Prefijo", "/api/v1/stock/*", "/api/v1/bi/*"],
                ["Número de endpoints", "7", "10 datasets."],
                [
                  "Operaciones",
                  "Lectura y ESCRITURA (entradas y salidas).",
                  "Solo lectura.",
                ],
                [
                  "Alcance de los datos",
                  "Un único cliente: el propietario del token.",
                  "La red completa: el cliente del token MÁS todas sus clínicas hijas.",
                ],
                [
                  "Autenticación",
                  "Bearer csk_live_ (middleware de stock).",
                  "Bearer csk_live_ (middleware de BI). El MISMO token.",
                ],
                [
                  "Exige gestión de stock activa",
                  "Sí, en los 7 endpoints. Si está inactiva, devuelve 403 stock_disabled.",
                  "Solo en 4 de los 10 datasets (inventario, lotes, movimientos y catalogo). Los otros 6 funcionan aunque la gestión de stock esté inactiva.",
                ],
                [
                  "Paginación",
                  "Ninguna. Solo /historial acepta limit (por defecto 100, tope 500); el resto devuelve el conjunto completo en una respuesta.",
                  "Keyset mediante cursor sobre un id ascendente. limit por defecto 500, tope 1000.",
                ],
                [
                  "Formato de respuesta",
                  "Un objeto con una clave por recurso: { productos: [...] }, { lotes: [...] }, { movimientos: [...] }.",
                  "Envoltura uniforme e idéntica en los 10 datasets: { data: [...], count, next_cursor }.",
                ],
                [
                  "Filtros de fecha",
                  "desde y hasta se inyectan sin validar ni normalizar: un formato inválido se compara como texto.",
                  "desde y hasta se validan contra YYYY-MM-DD. Un formato inválido se descarta EN SILENCIO. Cuatro datasets los ignoran por completo (son snapshot o dimensión).",
                ],
                [
                  "Grano de los datos",
                  "Producto, lote y movimiento de stock.",
                  "Línea de pedido, cabecera de pedido, transición de estado, existencias, lote, movimiento, producto, clínica y proveedor.",
                ],
                [
                  "Casos de uso típicos",
                  "ERP que descuenta consumo, lector de códigos de barras, robot de almacén, sincronización de inventario.",
                  "Cuadro de mando de compras, informe de gasto por clínica, análisis de lead time de proveedores, carga a un data warehouse.",
                ],
                [
                  "Consumidor habitual",
                  "Un servicio de backend que llama en tiempo real en cada operación.",
                  "Un proceso de carga programado (refresco diario u horario).",
                ],
              ]}
            />
            <p style={styles.p}>
              No es obligatorio usar las dos. Una integración de inventario puede ignorar por
              completo <code style={styles.codeInline}>/api/v1/bi/*</code>, y un equipo que solo
              construye informes no necesita tocar{" "}
              <code style={styles.codeInline}>/api/v1/stock/*</code>.
            </p>
          </Section>

          <Section id="autenticacion" title="Autenticación">
            <p style={styles.p}>
              Toda llamada requiere la cabecera <code style={styles.codeInline}>Authorization</code>{" "}
              con un token Bearer. El token tiene el prefijo{" "}
              <code style={styles.codeInline}>csk_live_</code> seguido de 64 caracteres
              hexadecimales (256 bits de entropía).
            </p>
            <Pre>{`Authorization: Bearer csk_live_4a8b9c1d2e3f...`}</Pre>
            <p style={styles.p}>
              Si la cabecera falta devolvemos <code style={styles.codeInline}>401 missing_token</code>;
              si no empieza por <code style={styles.codeInline}>csk_live_</code>,{" "}
              <code style={styles.codeInline}>401 invalid_token_format</code>; si el hash no
              coincide con ningún cliente, <code style={styles.codeInline}>401 invalid_token</code>.
            </p>

            <H3>Ciclo de vida del token</H3>
            <Pre>{`  ┌──────────────┐
  │  SIN TOKEN   │
  └──────┬───────┘
         │  POST /api/clientes/:id/stock-api-token
         │  (SOLO admin CentralSalud puede crear el primero)
         ▼
  ┌──────────────────────────────┐
  │  PENDIENTE DE RECLAMAR (24h) │   el valor en claro vive en BD con TTL
  └──────┬───────────────┬───────┘
         │               │  pasan 24h sin reclamar
         │               ▼
         │        ┌──────────────────────┐
         │        │ CADUCADO (410)       │──► hay que regenerar
         │        └──────────────────────┘
         │  GET  /api/clientes/:id/stock-api-token/claim
         │  (SOLO la clínica matriz; ni admin ni hijas)
         ▼
  ┌──────────────┐    POST .../stock-api-token  (regenerar)
  │   ACTIVO     │───────────────────────────────► vuelve a PENDIENTE
  └──────┬───────┘    el token anterior muere en el acto
         │  DELETE /api/clientes/:id/stock-api-token
         ▼
  ┌──────────────┐
  │   REVOCADO   │   todas las integraciones reciben 401 invalid_token
  └──────────────┘`}</Pre>

            <H3>Quién puede hacer qué</H3>
            <Table
              headers={["Acción", "Admin CentralSalud", "Clínica matriz", "Clínica hija"]}
              rows={[
                ["Crear el PRIMER token", "Sí", "No (403)", "No (403)"],
                ["Regenerar / rotar", "Sí", "Sí", "No (403)"],
                ["Revocar", "Sí", "Sí", "No (403)"],
                ["Ver estado (/info)", "Sí", "Sí", "No (403)"],
                ["Reclamar el valor en claro (/claim)", "No (403, a propósito)", "Sí", "No (403)"],
              ]}
            />
            <p style={styles.p}>
              El admin que emite el token <strong>nunca lo ve</strong>: la respuesta del POST solo
              devuelve <code style={styles.codeInline}>pending_until</code>. El valor en claro solo
              lo puede leer la matriz vía <code style={styles.codeInline}>/claim</code>, y una sola vez.
            </p>
            <p style={styles.p}>
              Las clínicas hijas no gestionan nada. Si una hija llama a{" "}
              <code style={styles.codeInline}>GET /api/mis-clientes-con-token</code> recibe{" "}
              <code style={styles.codeInline}>200</code> con una lista <strong>vacía</strong>, no un
              error. La matriz ve ahí su propio cliente más todas sus hijas con token activo.
            </p>

            <H3>Errores al reclamar el token</H3>
            <Table
              headers={["Status", "Código", "Cuándo"]}
              rows={[
                ["403", "—", "Llama un admin, una clínica hija o un usuario ajeno al cliente."],
                ["404", "no_pending_token", "No hay ningún token pendiente: o ya se reclamó, o nunca se generó."],
                ["410", "pending_token_expired", "Pasaron las 24h. El pendiente se borra y hay que regenerar."],
                ["404", "—", "El clienteId no existe."],
              ]}
            />
            <Callout type="warning" icon={<AlertTriangle size={16} />}>
              <strong>El token se muestra UNA SOLA VEZ.</strong> Tras emitirlo o regenerarlo, la
              matriz tiene <strong>24 horas</strong> para reclamarlo desde "API Keys". Después de la
              primera lectura desaparece. Si se pierde, la única salida es regenerar, y eso invalida
              el anterior al instante (rotación destructiva: no hay solapamiento, planifica la
              ventana de corte).
            </Callout>

            <H3>Solicitar acceso</H3>
            <p style={styles.p}>
              Escribe a{" "}
              <a href="mailto:soporte@centralsalud.es" style={styles.link}>soporte@centralsalud.es</a>{" "}
              indicando razón social, responsable de la integración (nombre, email, teléfono) y una
              descripción breve del caso de uso.
            </p>
          </Section>

          <Section id="gestion-token" title="Gestión del token (panel)">
            <p style={styles.p}>
              Los endpoints que emiten, reclaman, revocan y consultan el token{" "}
              <strong>no forman parte de ninguna de las dos APIs públicas</strong>: se autentican
              con la sesión del panel de CentralSalud (JWT), no con{" "}
              <code style={styles.codeInline}>csk_live_</code>, y no están pensados para
              automatizarse desde un sistema externo. Se documentan aquí para que quede claro qué
              ocurre por detrás cuando alguien emite o rota una credencial.
            </p>
            <Table
              headers={["Método", "Ruta", "Qué hace", "Quién puede"]}
              rows={[
                [
                  "POST",
                  "/api/clientes/:clienteId/stock-api-token",
                  "Emite un token nuevo. Si ya había uno, lo invalida en el acto. No devuelve el valor en claro: solo pending_until.",
                  "Admin de CentralSalud para el PRIMER token; admin o clínica matriz para regenerarlo.",
                ],
                [
                  "GET",
                  "/api/clientes/:clienteId/stock-api-token/claim",
                  "Lee y borra el valor en claro pendiente. Devuelve el token UNA sola vez.",
                  "Solo la clínica matriz. El admin recibe 403 de forma intencionada.",
                ],
                [
                  "GET",
                  "/api/clientes/:clienteId/stock-api-token/info",
                  "Estado del token sin revelarlo: fecha de creación, quién lo creó, última llamada, total de llamadas y si queda alguno pendiente de reclamar.",
                  "Admin o clínica matriz.",
                ],
                [
                  "DELETE",
                  "/api/clientes/:clienteId/stock-api-token",
                  "Revoca el token. Todas las integraciones pasan a recibir 401 invalid_token.",
                  "Admin o clínica matriz.",
                ],
                [
                  "GET",
                  "/api/mis-clientes-con-token",
                  "Lista los clientes con token activo visibles para el usuario, con sus metadatos de uso. Una clínica hija recibe 200 con la lista vacía.",
                  "Admin (ve todos) o clínica matriz (ve el suyo y el de sus hijas).",
                ],
              ]}
            />
            <Callout type="info" icon={<Key size={14} />}>
              <strong>Un token vale para las dos APIs.</strong> No existen credenciales separadas
              para stock y para BI, ni forma de emitir una restringida a una sola. Revocar corta el
              acceso a ambas de forma simultánea.
            </Callout>
          </Section>

          <Section id="scopes" title="Alcance y scopes">
            <Callout type="warning" icon={<AlertTriangle size={16} />}>
              <strong>El scope lo define el ENDPOINT, no el token.</strong> Un mismo{" "}
              <code style={styles.codeInline}>csk_live_</code> está limitado a un único cliente en{" "}
              <code style={styles.codeInline}>/api/v1/stock/*</code>, pero lee datos de{" "}
              <strong>toda la red (matriz + clínicas hijas)</strong> en{" "}
              <code style={styles.codeInline}>/api/v1/bi/*</code>. Si entregas el token de una matriz
              a un tercero, ese tercero puede leer los pedidos de todas sus hijas.
            </Callout>

            <Table
              headers={["Endpoints", "Alcance de los datos", "Cómo se resuelve"]}
              rows={[
                [
                  "/api/v1/stock/* (7 endpoints)",
                  "Un solo cliente: el dueño del token.",
                  "Todas las queries filtran por cliente_id = <dueño del token>.",
                ],
                [
                  "/api/v1/bi/* (10 datasets)",
                  "Red completa: el cliente del token MÁS todas sus clínicas hijas.",
                  "El middleware resuelve la red con WHERE id = ? OR clinica_matriz = ? y filtra por esa lista.",
                ],
              ]}
            />
            <p style={styles.p}>
              Si el token pertenece a un cliente <em>standalone</em> o a una clínica hija, la red se
              reduce a ese único cliente y los dos scopes coinciden en la práctica. La diferencia
              solo aparece cuando el token es de una <strong>matriz con hijas</strong>.
            </p>
            <p style={styles.p}>
              Ojo a una asimetría: el alcance de los <em>datos</em> es toda la red, pero el permiso
              para leer los cuatro datasets BI de stock se comprueba{" "}
              <strong>únicamente sobre el cliente dueño del token</strong>. Está desarrollado en{" "}
              <a href="#bi-datasets" style={styles.link}>API 2 · Datos: cómo funciona</a>.
            </p>

            <H3>Excepciones a tener en cuenta</H3>
            <Table
              headers={["Endpoint", "Excepción"]}
              rows={[
                [
                  "GET /api/v1/stock/producto-por-codigo/:codigo",
                  "Es un lookup GLOBAL: busca el código de barras en toda la tabla de códigos, sin filtrar por cliente. Devuelve el producto aunque no tengas stock de él ni lo hayas comprado nunca. No expone datos de otros clientes (solo la ficha del producto), pero conviene saberlo.",
                ],
                [
                  "POST /api/v1/stock/entradas",
                  "La validación del producto_id es global contra el catálogo de CentralSalud, no contra tu catálogo. Puedes dar de alta stock de cualquier producto existente en la plataforma; la fila de stock que se crea sí queda atada a tu cliente_id.",
                ],
              ]}
            />
            <p style={styles.p}>
              El <code style={styles.codeInline}>cliente_id</code> siempre se infiere del token: no
              se pasa en la URL ni en el body, y no hay forma de suplantarlo desde la petición.
            </p>
          </Section>

          <Section id="seguridad" title="Seguridad">
            <H3>Almacenamiento</H3>
            <p style={styles.p}>
              En base de datos solo vive el <strong>hash SHA-256</strong> del token, nunca el valor
              en claro. Si la base de datos se viese comprometida, los tokens no serían reversibles.
              La única excepción es la ventana de reclamación: al emitir o regenerar, el valor en
              claro queda guardado con un TTL de 24 horas y se borra en cuanto la matriz lo reclama
              (o al caducar).
            </p>

            <H3>Auditoría</H3>
            <p style={styles.p}>
              Cada llamada a la API queda registrada con timestamp, IP (respetando{" "}
              <code style={styles.codeInline}>X-Forwarded-For</code>), User-Agent, endpoint, método,
              status y un resumen de la operación. Sirve para forensia si un token se filtra o si
              detectamos uso anómalo. La auditoría nunca rompe la operación principal: si falla el
              registro, la petición sigue adelante.
            </p>

            <H3>Transporte</H3>
            <p style={styles.p}>
              Usa siempre <code style={styles.codeInline}>https://</code> de forma explícita en tus
              clientes y no confíes en una redirección automática. Nunca envíes el token por query
              string, logs o tickets de soporte: solo en la cabecera{" "}
              <code style={styles.codeInline}>Authorization</code>.
            </p>

            <H3>Rotación recomendada</H3>
            <p style={styles.p}>
              Rota el token al menos una vez al año, y siempre que alguien con acceso al sistema
              integrado deje la organización. Recuerda que la rotación es destructiva e inmediata:
              coordina el despliegue del nuevo valor antes de regenerar.
            </p>
          </Section>

          <Section id="errores" title="Errores">
            <p style={styles.p}>Todos los errores devuelven JSON con la forma:</p>
            <Pre>{`{
  "error": "codigo_maquina",
  "message": "Descripción legible del error"
}`}</Pre>
            <Table
              headers={["Status", "Código", "Cuándo"]}
              rows={[
                ["400", "bad_request", "Faltan producto_id o cantidad_unidades, o la cantidad no es un entero > 0."],
                ["400", "insufficient_stock", "Salida mayor que el stock disponible. Incluye el campo extra cantidad_disponible."],
                ["400", "lot_insufficient", "Pediste un lote concreto y ese lote no existe, está agotado o no cubre toda la cantidad."],
                ["400", "insufficient_stock_lots", "El total de stock alcanza pero los lotes no suman lo pedido: hay desincronización entre stock y lotes. Avisa a soporte."],
                ["401", "missing_token", "Falta la cabecera Authorization."],
                ["401", "invalid_token_format", "El token no empieza por csk_live_."],
                ["401", "invalid_token", "El token no existe, fue revocado o fue regenerado."],
                ["403", "stock_disabled", "El cliente dueño del token tiene la gestión de stock inactiva. Afecta a los 7 endpoints de /api/v1/stock/* y a los 4 datasets BI de stock: inventario, lotes, movimientos y catalogo."],
                ["404", "product_not_found", "El producto_id no existe en el catálogo de CentralSalud."],
                ["404", "barcode_not_found", "El código de barras no está asociado a ningún producto."],
                ["500", "auth_error", "Fallo al validar el token (normalmente base de datos). El token puede ser válido: reinténtalo."],
                ["500", "internal_error", "Error nuestro. Si persiste, contacta con soporte."],
              ]}
            />
            <Callout type="info" icon={<Shield size={16} />}>
              <strong>En BI, el <code style={styles.codeInline}>403 stock_disabled</code> depende del
              dataset.</strong> Los seis datasets de pedidos y dimensiones (
              <code style={styles.codeInline}>ventas-lineas</code>,{" "}
              <code style={styles.codeInline}>estados-pedido</code>,{" "}
              <code style={styles.codeInline}>pedidos</code>,{" "}
              <code style={styles.codeInline}>facturacion</code>,{" "}
              <code style={styles.codeInline}>clinicas</code> y{" "}
              <code style={styles.codeInline}>proveedores</code>) responden{" "}
              <code style={styles.codeInline}>200</code> aunque la gestión de stock esté inactiva. Los
              cuatro que exponen stock (<code style={styles.codeInline}>inventario</code>,{" "}
              <code style={styles.codeInline}>lotes</code>,{" "}
              <code style={styles.codeInline}>movimientos</code> y{" "}
              <code style={styles.codeInline}>catalogo</code>) devuelven{" "}
              <code style={styles.codeInline}>403</code> con el mismísimo token. Detalle completo en{" "}
              <a href="#bi-datasets" style={styles.link}>API 2 · Datos: cómo funciona</a>.
            </Callout>
            <p style={styles.p}>
              Un <code style={styles.codeInline}>500</code> nunca deja escrituras a medias: entradas y
              salidas corren dentro de una transacción con rollback.
            </p>
          </Section>

          {/* ────────── API 1 · Stock (/api/v1/stock) ────────── */}
          {verStock && (
            <>
              <StockIntro />
              <EndpointEntradas />
              <EndpointSalidas />
              <EndpointInventario />
              <EndpointLotes />
              <EndpointHistorial />
              <EndpointCatalogo />
              <EndpointBarcode />
            </>
          )}

          {/* ────────── API 2 · Datos / BI (/api/v1/bi) ────────── */}
          {verBi && (
            <>
              <BiIntro />
              <EndpointBiVentas />
              <EndpointBiPedidos />
              <EndpointBiEstados />
              <EndpointBiFacturacion />
              <EndpointBiInventario />
              <EndpointBiLotes />
              <EndpointBiMovimientos />
              <EndpointBiCatalogo />
              <EndpointBiClinicas />
              <EndpointBiProveedores />
              <BiPowerQuery />
            </>
          )}

          <Section id="changelog" title="Changelog">
            <Table
              headers={["Versión", "Fecha", "Cambios"]}
              rows={[
                [
                  "v1.3",
                  "2026-07-21",
                  "La API de Datos (BI) pasa de 2 a 10 datasets. Nuevos: pedidos, facturacion, clinicas y proveedores (abiertos), e inventario, lotes, movimientos y catalogo (requieren el servicio de gestión de stock contratado; devuelven 403 stock_disabled si no). Se añade una tabla resumen de los diez datasets, se documenta que el flag gestion_stock se comprueba sobre el cliente DUEÑO DEL TOKEN y no sobre cada clínica de la red, se advierte de que desde y hasta se ignoran en inventario, catalogo, clinicas y proveedores, y se avisa de que el campo cantidad significa cosas distintas en ventas-lineas, inventario y lotes. Corregida la afirmación previa de que los endpoints BI nunca devuelven 403.",
                ],
                [
                  "v1.2",
                  "2026-07-20",
                  "Solo documentación, sin cambios en la API. El documento se reestructura para distinguir explícitamente las DOS APIs: se añade una tabla comparativa (alcance, autenticación, paginación, formato de respuesta y casos de uso), una portada propia para cada una y una sección con los endpoints de gestión del token, que se autentican por sesión del panel y no con csk_live_. Texto revisado a español peninsular.",
                ],
                [
                  "v1.1",
                  "2026-07-20",
                  "Documentados los datasets BI (ventas-lineas y estados-pedido) con paginación keyset, diccionario de estados y ejemplo Power Query. Corregido el alcance del token: el scope lo define el endpoint, no la credencial. Documentadas las reglas reales de bajo_minimo, catálogo, FEFO, truncado de motivo, lotes autogenerados, límites del historial y lotes agotados. Añadidos los códigos insufficient_stock_lots y auth_error. Base URL actualizada al host público de producción.",
                ],
                [
                  "v1.0",
                  "2026-05-22",
                  "Lanzamiento inicial: entradas, salidas, inventario, lotes, historial, catálogo y resolución de códigos de barras.",
                ],
              ]}
            />
          </Section>

          <Section id="contacto" title="Contacto y soporte">
            <p style={styles.p}>
              Para dudas técnicas o reportes de seguridad:{" "}
              <a href="mailto:soporte@centralsalud.es" style={styles.link}>
                <Mail size={13} style={{ verticalAlign: "middle", marginRight: 4 }} />
                soporte@centralsalud.es
              </a>
            </p>
            <p style={styles.p}>
              <strong>Reportes de seguridad responsables:</strong> agradecemos divulgación
              coordinada. Si detectas una vulnerabilidad, contáctanos antes de hacerla pública.
            </p>
          </Section>

          <footer style={styles.footer}>
            <p>CentralSalud · APIs v1 (Stock y Datos/BI) · Documentación pública para integradores</p>
          </footer>
        </main>
      </div>
    </div>
  );
}

// ─── Sidebar ────────────────────────────────────────────────────────────────
function Sidebar({ activeId, api }) {
  // grupo: 'comun' siempre visible · 'stock' / 'bi' según la vista.
  const grupoApi = api === 'stock' ? 'stock' : api === 'datos' ? 'bi' : null;
  const nav = [
    { type: "section", label: "Empieza aquí", grupo: "comun" },
    { type: "link", id: "introduccion", label: "Introducción", grupo: "comun" },
    { type: "link", id: "comparativa", label: "Las dos APIs", grupo: "comun" },
    { type: "section", label: "Común a las dos APIs", grupo: "comun" },
    { type: "link", id: "autenticacion", label: "Autenticación", grupo: "comun" },
    { type: "link", id: "gestion-token", label: "Gestión del token", grupo: "comun" },
    { type: "link", id: "scopes", label: "Alcance y scopes", grupo: "comun" },
    { type: "link", id: "seguridad", label: "Seguridad", grupo: "comun" },
    { type: "link", id: "errores", label: "Errores", grupo: "comun" },
    { type: "section", label: "API 1 · Stock · /api/v1/stock", grupo: "stock" },
    { type: "link", id: "stock-api", label: "Cómo funciona", grupo: "stock" },
    { type: "link", id: "post-entradas", label: "Registrar entrada", method: "POST", grupo: "stock" },
    { type: "link", id: "post-salidas", label: "Registrar salida", method: "POST", grupo: "stock" },
    { type: "link", id: "get-inventario", label: "Consultar inventario", method: "GET", grupo: "stock" },
    { type: "link", id: "get-lotes", label: "Consultar lotes", method: "GET", grupo: "stock" },
    { type: "link", id: "get-historial", label: "Historial", method: "GET", grupo: "stock" },
    { type: "link", id: "get-catalogo", label: "Catálogo", method: "GET", grupo: "stock" },
    { type: "link", id: "get-barcode", label: "Resolver código", method: "GET", grupo: "stock" },
    { type: "section", label: "API 2 · Datos / BI · /api/v1/bi", grupo: "bi" },
    { type: "link", id: "bi-datasets", label: "Cómo funcionan", grupo: "bi" },
    { type: "section", label: "BI · Pedidos y facturación", grupo: "bi" },
    { type: "link", id: "get-bi-ventas", label: "Ventas por línea", method: "GET", grupo: "bi" },
    { type: "link", id: "get-bi-pedidos", label: "Pedidos", method: "GET", grupo: "bi" },
    { type: "link", id: "get-bi-estados", label: "Estados de pedido", method: "GET", grupo: "bi" },
    { type: "link", id: "get-bi-facturacion", label: "Facturación", method: "GET", grupo: "bi" },
    { type: "section", label: "BI · Stock (requiere servicio)", grupo: "bi" },
    { type: "link", id: "get-bi-inventario", label: "Inventario", method: "GET", grupo: "bi" },
    { type: "link", id: "get-bi-lotes", label: "Lotes", method: "GET", grupo: "bi" },
    { type: "link", id: "get-bi-movimientos", label: "Movimientos", method: "GET", grupo: "bi" },
    { type: "link", id: "get-bi-catalogo", label: "Catálogo", method: "GET", grupo: "bi" },
    { type: "section", label: "BI · Dimensiones", grupo: "bi" },
    { type: "link", id: "get-bi-clinicas", label: "Clínicas", method: "GET", grupo: "bi" },
    { type: "link", id: "get-bi-proveedores", label: "Proveedores", method: "GET", grupo: "bi" },
    { type: "section", label: "BI · Herramientas", grupo: "bi" },
    { type: "link", id: "bi-powerquery", label: "Excel / Power Query", grupo: "bi" },
    { type: "section", label: "Recursos", grupo: "comun" },
    { type: "link", id: "changelog", label: "Changelog", grupo: "comun" },
    { type: "link", id: "contacto", label: "Contacto", grupo: "comun" },
  ].filter((it) => !grupoApi || it.grupo === "comun" || it.grupo === grupoApi);
  return (
    <aside style={styles.sidebar}>
      <div style={styles.brand}>
        <div style={styles.brandIcon}>
          <img src="/image.png" alt="CentralSalud" style={{ width: 28, height: 28, objectFit: "contain" }} />
        </div>
        <div>
          <div style={{ fontWeight: 800, fontSize: 15 }}>CentralSalud</div>
          <div style={{ fontSize: 11, color: "#6b7280" }}>
            {api === 'stock' ? 'API de Stock · v1' : api === 'datos' ? 'API de Datos (BI) · v1' : 'Stock + Datos (BI) · v1'}
          </div>
        </div>
      </div>
      <nav>
        {nav.map((item, i) =>
          item.type === "section" ? (
            <div key={i} style={styles.navSection}>{item.label}</div>
          ) : (
            <a
              key={i}
              href={`#${item.id}`}
              style={{
                ...styles.navLink,
                ...(activeId === item.id ? styles.navLinkActive : null),
              }}
            >
              {item.method && <MethodTag method={item.method} small />}
              <span style={{ marginLeft: item.method ? 6 : 0 }}>{item.label}</span>
            </a>
          )
        )}
      </nav>
    </aside>
  );
}

// ─── Hero header ───────────────────────────────────────────────────────────
function Hero() {
  return (
    <div style={{ marginBottom: 32 }}>
      <h1 style={styles.h1}>
        API de CentralSalud{" "}
        <span style={styles.versionBadge}>v1</span>
      </h1>
      <p style={styles.lead}>
        Dos APIs REST independientes bajo un mismo host y una misma credencial. La{" "}
        <strong>API de Stock</strong> permite que tu sistema externo registre entradas y salidas por
        HTTP y consulte el inventario de una clínica (los cambios se reflejan al instante en la web
        y en la app móvil). La <strong>API de Datos (BI)</strong> vuelca los pedidos de toda tu red
        en Power BI, Excel o tu propio data warehouse.
      </p>
    </div>
  );
}

function RequisitoPrevio() {
  return (
    <Callout type="info" icon={<Shield size={16} />}>
      <strong>Requisito previo:</strong> hay que ser cliente de CentralSalud y tener un token
      emitido. La <strong>API de Stock</strong> exige además tener contratada la gestión de stock; en
      la <strong>API de Datos (BI)</strong> solo la exigen 4 de sus 10 datasets, y los otros 6
      funcionan siempre. Si todavía no tienes acceso, escribe a{" "}
      <a href="mailto:soporte@centralsalud.es" style={{ ...styles.link, color: PRIMARY_DARK }}>
        soporte@centralsalud.es
      </a>.
    </Callout>
  );
}

// Helper: genera tabs de ejemplo para un GET sin body, con query opcional.
// Evita repetir el mismo boilerplate en cada endpoint GET.
function getRequestTabs(pathRelativo, queryString = "") {
  const url = `${BASE_URL}${pathRelativo}${queryString}`;
  return [
    {
      label: "curl",
      code: `curl "${url}" \\
  -H "Authorization: Bearer csk_live_xxx..."`,
    },
    {
      label: "Node.js",
      code: `const r = await fetch('${url}', {
  headers: { 'Authorization': \`Bearer \${process.env.CSK_TOKEN}\` },
});
console.log(await r.json());`,
    },
    {
      label: "Python",
      code: `import os, requests
r = requests.get(
    "${url}",
    headers={"Authorization": f"Bearer {os.environ['CSK_TOKEN']}"},
)
print(r.json())`,
    },
    {
      label: "PHP",
      code: `<?php
$ch = curl_init('${url}');
curl_setopt_array($ch, [
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_HTTPHEADER     => [
        'Authorization: Bearer ' . getenv('CSK_TOKEN'),
    ],
]);
print_r(json_decode(curl_exec($ch), true));`,
    },
  ];
}


// ─── API 1 · Stock (/api/v1/stock) ─────────────────────────────────────────
function StockIntro() {
  return (
    <Section id="stock-api" title="API 1 · Stock: cómo funciona">
      <p style={styles.p}>
        Aquí empieza la <strong>primera API</strong>. Los siete endpoints de{" "}
        <code style={styles.codeInline}>/api/v1/stock/*</code> operan sobre el inventario de{" "}
        <strong>un único cliente</strong>: el propietario del token. Es la única de las dos que
        escribe en la plataforma, y la única que exige tener la gestión de stock activa.
      </p>
      <Table
        headers={["Endpoint", "Método", "Para qué"]}
        rows={[
          ["/api/v1/stock/entradas", "POST", "Sumar unidades y asociarlas a un lote."],
          ["/api/v1/stock/salidas", "POST", "Restar unidades, con FEFO automático o lote concreto."],
          ["/api/v1/stock/inventario", "GET", "Stock actual, un registro por producto."],
          ["/api/v1/stock/lotes", "GET", "Lotes con su fecha de vencimiento y cantidad restante."],
          ["/api/v1/stock/historial", "GET", "Movimientos de stock, del más reciente al más antiguo."],
          ["/api/v1/stock/catalogo", "GET", "Productos que ya tienen fila de stock para tu cliente."],
          ["/api/v1/stock/producto-por-codigo/:codigo", "GET", "Resolver un código de barras a producto."],
        ]}
      />
      <p style={styles.p}>
        Ninguno de ellos pagina. El único que acepta{" "}
        <code style={styles.codeInline}>limit</code> es{" "}
        <a href="#get-historial" style={styles.link}>/historial</a>. Para volcados masivos de
        pedidos, la herramienta correcta es la{" "}
        <a href="#bi-datasets" style={styles.link}>API de Datos (BI)</a>.
      </p>
    </Section>
  );
}

function EndpointEntradas() {
  return (
    <Section id="post-entradas" title="Registrar entrada">
      <EndpointCard
        method="POST"
        path="/api/v1/stock/entradas"
        summary="Suma unidades al stock del cliente y las asocia a un lote (existente o nuevo). Si el producto no tenía fila de stock, se crea."
      >
        <H4>Body</H4>
        <Table
          headers={["Campo", "Tipo", "Req", "Descripción"]}
          rows={[
            ["producto_id", "integer", "sí", "ID del producto en el catálogo de CentralSalud. Si no existe: 404 product_not_found."],
            ["cantidad_unidades", "integer", "sí", "Unidades a sumar. Tiene que ser un entero > 0."],
            ["lote", "string", "no", 'Identificador del lote (ej. "L240115A"). Si no lo indicas, se genera uno con el formato API-<epoch_ms>, por ejemplo API-1753012345678.'],
            ["fecha_vencimiento", "string", "no", "Formato YYYY-MM-DD. Solo se aplica al CREAR el lote."],
            ["motivo", "string", "no", "Texto libre. Se trunca a 100 caracteres y se le antepone el prefijo [API] ."],
            ["referencia_externa", "string", "no", "Tu identificador. Se concatena al motivo como (ref:TU-VALOR). No se trunca ni se indexa."],
          ]}
        />

        <H4>Comportamiento a tener en cuenta</H4>
        <ul style={styles.ul}>
          <li>
            Si ya existe un lote con ese mismo <code style={styles.codeInline}>lote</code> para el
            producto, se <strong>suma</strong> a su cantidad en lugar de crear otro.
          </li>
          <li>
            Sobre un lote existente, <code style={styles.codeInline}>fecha_vencimiento</code> se
            aplica con <code style={styles.codeInline}>COALESCE</code>:{" "}
            <strong>no sobrescribe una fecha ya puesta</strong>. Solo rellena si estaba vacía. Para
            corregir una fecha equivocada hay que hacerlo desde el panel.
          </li>
          <li>
            Si el producto no tenía fila de stock, se crea con{" "}
            <code style={styles.codeInline}>minimo = 0</code> y{" "}
            <code style={styles.codeInline}>maximo = 0</code>. Importante: con{" "}
            <code style={styles.codeInline}>minimo = 0</code> ese producto{" "}
            <strong>nunca aparecerá</strong> en{" "}
            <code style={styles.codeInline}>inventario?bajo_minimo=true</code>.
          </li>
          <li>
            El movimiento queda registrado con{" "}
            <code style={styles.codeInline}>metodo = "api"</code>, así que es distinguible del
            movimiento manual en el historial.
          </li>
          <li>
            Toda la operación es transaccional: o se aplica entera o no se aplica nada.
          </li>
        </ul>

        <H4>Ejemplos</H4>
        <CodeTabs
          tabs={[
            {
              label: "curl",
              code: `curl -X POST ${BASE_URL}/api/v1/stock/entradas \\
  -H "Authorization: Bearer csk_live_xxx..." \\
  -H "Content-Type: application/json" \\
  -d '{
    "producto_id": 12345,
    "cantidad_unidades": 100,
    "lote": "L240115A",
    "fecha_vencimiento": "2026-12-31",
    "referencia_externa": "REF-2026-001234"
  }'`,
            },
            {
              label: "Node.js",
              code: `const r = await fetch('${BASE_URL}/api/v1/stock/entradas', {
  method: 'POST',
  headers: {
    'Authorization': \`Bearer \${process.env.CSK_TOKEN}\`,
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    producto_id: 12345,
    cantidad_unidades: 100,
    lote: 'L240115A',
    fecha_vencimiento: '2026-12-31',
    referencia_externa: 'REF-2026-001234',
  }),
});
console.log(await r.json());`,
            },
            {
              label: "Python",
              code: `import os, requests

r = requests.post(
    "${BASE_URL}/api/v1/stock/entradas",
    headers={"Authorization": f"Bearer {os.environ['CSK_TOKEN']}"},
    json={
        "producto_id": 12345,
        "cantidad_unidades": 100,
        "lote": "L240115A",
        "fecha_vencimiento": "2026-12-31",
        "referencia_externa": "REF-2026-001234",
    },
    timeout=10,
)
print(r.json())`,
            },
            {
              label: "PHP",
              code: `<?php
$ch = curl_init('${BASE_URL}/api/v1/stock/entradas');
curl_setopt_array($ch, [
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_POST           => true,
    CURLOPT_HTTPHEADER     => [
        'Authorization: Bearer ' . getenv('CSK_TOKEN'),
        'Content-Type: application/json',
    ],
    CURLOPT_POSTFIELDS     => json_encode([
        'producto_id'        => 12345,
        'cantidad_unidades'  => 100,
        'lote'               => 'L240115A',
        'fecha_vencimiento'  => '2026-12-31',
        'referencia_externa' => 'REF-2026-001234',
    ]),
]);
print_r(json_decode(curl_exec($ch), true));`,
            },
          ]}
        />

        <H4>Respuesta 200</H4>
        <Pre>{`{
  "ok": true,
  "producto_id": 12345,
  "cantidad_anterior": 40,
  "cantidad_nueva": 140,
  "lote": "L240115A",
  "lote_id": 5891
}`}</Pre>

        <H4>Códigos</H4>
        <Table
          headers={["Status", "Cuándo"]}
          rows={[
            ["200", "Entrada registrada."],
            ["400 bad_request", "Falta producto_id o cantidad_unidades, o la cantidad no es > 0."],
            ["401", "Token ausente, mal formado o inválido."],
            ["403 stock_disabled", "Gestión de stock inactiva."],
            ["404 product_not_found", "El producto_id no existe."],
            ["500 internal_error", "Error nuestro. Se hace rollback: no queda nada a medias."],
          ]}
        />
      </EndpointCard>
    </Section>
  );
}

function EndpointSalidas() {
  return (
    <Section id="post-salidas" title="Registrar salida">
      <EndpointCard
        method="POST"
        path="/api/v1/stock/salidas"
        summary="Resta unidades del stock. Sin lote aplica FEFO automático; con lote consume ese y solo ese."
      >
        <H4>Body</H4>
        <Table
          headers={["Campo", "Tipo", "Req", "Descripción"]}
          rows={[
            ["producto_id", "integer", "sí", "ID del producto."],
            ["cantidad_unidades", "integer", "sí", "Unidades a restar. Entero > 0."],
            ["lote", "string", "no", "Si lo indicas, se consume ESE lote y tiene que cubrir la cantidad entera. Si no, FEFO automático."],
            ["motivo", "string", "no", "Texto libre. Se trunca a 100 caracteres y se prefija con [API] ."],
            ["referencia_externa", "string", "no", "Se concatena al motivo como (ref:TU-VALOR)."],
          ]}
        />

        <H4>Cómo elige los lotes</H4>
        <ul style={styles.ul}>
          <li>
            <strong>Sin <code style={styles.codeInline}>lote</code> (FEFO):</strong> recorre los
            lotes con cantidad {">"} 0 ordenados por vencimiento más próximo primero (los que no
            tienen fecha van al final) y, a igualdad, por cantidad ascendente. Va consumiendo hasta
            completar la cantidad, <strong>repartiendo entre varios lotes si hace falta</strong>. La
            respuesta lista todos los lotes tocados.
          </li>
          <li>
            <strong>Con <code style={styles.codeInline}>lote</code>:</strong> ese lote concreto tiene
            que cubrir <strong>toda</strong> la cantidad él solo.{" "}
            <strong>No reparte</strong>: si el lote tiene 3 y pides 5, falla con{" "}
            <code style={styles.codeInline}>lot_insufficient</code> aunque haya 50 unidades en otros
            lotes.
          </li>
          <li>
            Se genera un movimiento por cada lote consumido (trazabilidad), todos con el mismo
            motivo.
          </li>
          <li>
            Si la salida deja el producto bajo mínimo, se dispara la notificación de bajo stock
            igual que en el flujo manual.
          </li>
        </ul>

        <H4>Ejemplos</H4>
        <CodeTabs
          tabs={[
            {
              label: "curl",
              code: `curl -X POST ${BASE_URL}/api/v1/stock/salidas \\
  -H "Authorization: Bearer csk_live_xxx..." \\
  -H "Content-Type: application/json" \\
  -d '{"producto_id": 12345, "cantidad_unidades": 5}'`,
            },
            {
              label: "Node.js",
              code: `const r = await fetch('${BASE_URL}/api/v1/stock/salidas', {
  method: 'POST',
  headers: {
    'Authorization': \`Bearer \${process.env.CSK_TOKEN}\`,
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({ producto_id: 12345, cantidad_unidades: 5 }),
});
console.log(await r.json());`,
            },
            {
              label: "Python",
              code: `import os, requests
r = requests.post(
    "${BASE_URL}/api/v1/stock/salidas",
    headers={"Authorization": f"Bearer {os.environ['CSK_TOKEN']}"},
    json={"producto_id": 12345, "cantidad_unidades": 5},
)
print(r.json())`,
            },
            {
              label: "PHP",
              code: `<?php
$ch = curl_init('${BASE_URL}/api/v1/stock/salidas');
curl_setopt_array($ch, [
    CURLOPT_RETURNTRANSFER => true,
    CURLOPT_POST           => true,
    CURLOPT_HTTPHEADER     => [
        'Authorization: Bearer ' . getenv('CSK_TOKEN'),
        'Content-Type: application/json',
    ],
    CURLOPT_POSTFIELDS     => json_encode([
        'producto_id'       => 12345,
        'cantidad_unidades' => 5,
    ]),
]);
print_r(json_decode(curl_exec($ch), true));`,
            },
          ]}
        />

        <H4>Respuesta 200</H4>
        <Pre>{`{
  "ok": true,
  "producto_id": 12345,
  "cantidad_anterior": 140,
  "cantidad_nueva": 135,
  "unidades_consumidas": 5,
  "lotes_consumidos": [
    { "lote": "L240115A", "unidades": 3 },
    { "lote": "L240220B", "unidades": 2 }
  ]
}`}</Pre>

        <H4>Respuesta 400 · stock insuficiente</H4>
        <p style={styles.p}>
          Este error trae un campo extra,{" "}
          <code style={styles.codeInline}>cantidad_disponible</code>, para que no tengas que hacer
          una segunda llamada a <code style={styles.codeInline}>/inventario</code>:
        </p>
        <Pre>{`{
  "error": "insufficient_stock",
  "message": "Stock insuficiente. Disponible: 2, solicitado: 5",
  "cantidad_disponible": 2
}`}</Pre>

        <H4>Códigos</H4>
        <Table
          headers={["Status", "Cuándo"]}
          rows={[
            ["200", "Salida registrada."],
            ["400 bad_request", "Falta producto_id o cantidad_unidades, o no es > 0."],
            ["400 insufficient_stock", "El stock total no llega. Incluye cantidad_disponible."],
            ["400 lot_insufficient", "El lote indicado no existe, está agotado o no cubre la cantidad entera."],
            ["400 insufficient_stock_lots", "El stock total llega pero los lotes no suman: stock y lotes están desincronizados. Repórtalo a soporte."],
            ["401 / 403", "Token inválido o gestión de stock inactiva."],
            ["500 internal_error", "Error nuestro, con rollback."],
          ]}
        />
      </EndpointCard>
    </Section>
  );
}

function EndpointInventario() {
  return (
    <Section id="get-inventario" title="Consultar inventario">
      <EndpointCard
        method="GET"
        path="/api/v1/stock/inventario"
        summary="Stock actual del cliente, un registro por producto, ordenado por nombre."
      >
        <H4>Query params</H4>
        <Table
          headers={["Param", "Tipo", "Descripción"]}
          rows={[
            ["producto_id", "integer", "Devuelve solo ese producto."],
            ["bajo_minimo", "string", 'Solo se activa con el valor exacto "true". Cualquier otro valor se ignora.'],
          ]}
        />

        <Callout type="warning" icon={<AlertTriangle size={16} />}>
          <strong><code style={styles.codeInline}>bajo_minimo=true</code> filtra por{" "}
          <code style={styles.codeInline}>cantidad {"<"} minimo AND minimo {">"} 0</code>.</strong>{" "}
          Los productos con <code style={styles.codeInline}>minimo = 0</code>{" "}
          <strong>nunca aparecen</strong>, ni siquiera con stock cero. Es intencional (sin mínimo
          definido no hay umbral que cruzar), pero sorprende: los productos creados automáticamente
          por <code style={styles.codeInline}>/entradas</code> nacen con{" "}
          <code style={styles.codeInline}>minimo = 0</code>. Configura los mínimos desde el panel si
          quieres que aparezcan.
        </Callout>

        <H4>Campos devueltos</H4>
        <Table
          headers={["Campo", "Descripción"]}
          rows={[
            ["producto_id", "ID a usar en entradas y salidas."],
            ["nombre", "Nombre del producto."],
            ["ref_proveedor", "Referencia del proveedor."],
            ["unidadbasica", 'Texto descriptivo del embalaje, por ejemplo "1 caja de 100".'],
            ["cantidad", "Unidades actuales en stock."],
            ["minimo", "Umbral de bajo mínimo. 0 = sin umbral definido."],
            ["maximo", "Umbral máximo. 0 = sin umbral definido."],
          ]}
        />

        <H4>Ejemplos</H4>
        <CodeTabs tabs={getRequestTabs("/api/v1/stock/inventario", "?bajo_minimo=true")} />
        <H4>Respuesta 200</H4>
        <Pre>{`{
  "productos": [
    {
      "producto_id": 12345,
      "nombre": "Jeringa 5ml",
      "ref_proveedor": "JER-5ML",
      "unidadbasica": "1 caja de 100",
      "cantidad": 135,
      "minimo": 50,
      "maximo": 500
    }
  ]
}`}</Pre>
        <Callout type="info" icon={<Key size={14} />}>
          <strong>Sin paginación ni tope.</strong> Devuelve el inventario completo en una sola
          respuesta. Si tienes miles de referencias, cuenta con payloads grandes y cachea en el lado del
          cliente en vez de sondear en bucle.
        </Callout>
      </EndpointCard>
    </Section>
  );
}

function EndpointLotes() {
  return (
    <Section id="get-lotes" title="Consultar lotes">
      <EndpointCard
        method="GET"
        path="/api/v1/stock/lotes"
        summary="Lotes del cliente ordenados por FEFO: primero los que tienen fecha de vencimiento más próxima, al final los que no tienen fecha."
      >
        <H4>Query params</H4>
        <Table
          headers={["Param", "Tipo", "Descripción"]}
          rows={[["producto_id", "integer", "Filtra a un solo producto."]]}
        />

        <Callout type="warning" icon={<AlertTriangle size={16} />}>
          <strong>Devuelve también los lotes agotados</strong> (
          <code style={styles.codeInline}>cantidad = 0</code>). No hay filtro para excluirlos: si
          solo te interesa el stock vivo, fíltralo tú por{" "}
          <code style={styles.codeInline}>cantidad {">"} 0</code>. Tampoco hay paginación ni tope.
        </Callout>

        <H4>Campos devueltos</H4>
        <Table
          headers={["Campo", "Descripción"]}
          rows={[
            ["lote_id", "ID interno del lote."],
            ["lote", "Identificador del lote tal cual se registró (puede ser API-<epoch_ms> si fue autogenerado)."],
            ["fecha_vencimiento", "YYYY-MM-DD o null si no se informó."],
            ["cantidad", "Unidades restantes en ese lote. Puede ser 0."],
            ["created_at", "Cuándo se creó el lote."],
            ["producto_id", "Producto al que pertenece."],
            ["nombre", "Nombre del producto."],
            ["ref_proveedor", "Referencia del proveedor."],
          ]}
        />

        <H4>Ejemplos</H4>
        <CodeTabs tabs={getRequestTabs("/api/v1/stock/lotes", "?producto_id=12345")} />
        <H4>Respuesta 200</H4>
        <Pre>{`{
  "lotes": [
    {
      "lote_id": 5891,
      "lote": "L240115A",
      "fecha_vencimiento": "2026-12-31",
      "cantidad": 100,
      "created_at": "2026-05-22T09:15:32.000Z",
      "producto_id": 12345,
      "nombre": "Jeringa 5ml",
      "ref_proveedor": "JER-5ML"
    }
  ]
}`}</Pre>
      </EndpointCard>
    </Section>
  );
}

function EndpointHistorial() {
  return (
    <Section id="get-historial" title="Historial de movimientos">
      <EndpointCard
        method="GET"
        path="/api/v1/stock/historial"
        summary="Movimientos de stock del cliente, del más reciente al más antiguo. Incluye los hechos desde la web, la app móvil y la API."
      >
        <H4>Query params</H4>
        <Table
          headers={["Param", "Tipo", "Descripción"]}
          rows={[
            ["producto_id", "integer", "Filtra a un solo producto."],
            ["desde", "string", "Límite inferior sobre el campo fecha, inclusivo. Se compara tal cual, sin validar el formato."],
            ["hasta", "string", "Límite superior sobre el campo fecha, inclusivo. Se compara tal cual, sin validar el formato. Lee el aviso de abajo."],
            ["limit", "integer", "Por defecto 100, tope 500. Valores no numéricos o 0 caen a 100."],
          ]}
        />

        <Callout type="warning" icon={<AlertTriangle size={16} />}>
          <strong>Cuidado con <code style={styles.codeInline}>hasta</code>.</strong>{" "}
          <code style={styles.codeInline}>desde</code> y{" "}
          <code style={styles.codeInline}>hasta</code> se inyectan sin validar ni normalizar y se
          comparan contra un campo con fecha <em>y hora</em>. Como consecuencia,{" "}
          <code style={styles.codeInline}>hasta=2026-07-20</code> equivale a{" "}
          <code style={styles.codeInline}>2026-07-20 00:00:00</code> y{" "}
          <strong>excluye ese día entero</strong>. Para incluirlo, envía{" "}
          <code style={styles.codeInline}>hasta=2026-07-20 23:59:59</code> (URL-encoded) o pide el
          día siguiente. Un formato de fecha inválido no da error: MySQL lo compara como texto y el
          resultado es impredecible.
        </Callout>

        <ul style={styles.ul}>
          <li>
            <code style={styles.codeInline}>limit</code> se acota con{" "}
            <code style={styles.codeInline}>min(500, valor)</code>. Valores negativos provocan un{" "}
            <code style={styles.codeInline}>500</code>: no los mandes.
          </li>
          <li>
            <strong>No hay paginación.</strong> Para recorrer un historial largo, segmenta por
            fechas con <code style={styles.codeInline}>desde</code> /{" "}
            <code style={styles.codeInline}>hasta</code>. Si necesitas volcados masivos de pedidos,
            usa los <a href="#bi-datasets" style={styles.link}>datasets BI</a>, que sí paginan.
          </li>
          <li>
            <code style={styles.codeInline}>metodo</code> te dice el origen:{" "}
            <code style={styles.codeInline}>api</code> para lo que entra por aquí, otros valores para
            web y app.
          </li>
        </ul>

        <H4>Campos devueltos</H4>
        <Table
          headers={["Campo", "Descripción"]}
          rows={[
            ["id", "ID del movimiento."],
            ["producto_id", "Producto afectado."],
            ["producto_nombre", "Nombre del producto."],
            ["ref_proveedor", "Referencia del proveedor."],
            ["tipo", 'entrada o salida.'],
            ["cantidad_anterior", "Stock antes del movimiento."],
            ["cantidad_nueva", "Stock después del movimiento."],
            ["delta", "Diferencia aplicada. Negativa en las salidas."],
            ["motivo", "Texto del movimiento. Los de la API llevan el prefijo [API] ."],
            ["metodo", 'Origen del movimiento: "api", o el valor correspondiente a web / app.'],
            ["fecha", "Timestamp del movimiento."],
            ["lote_numero", "Lote asociado, o null si el movimiento no tenía lote."],
            ["fecha_vencimiento", "Vencimiento de ese lote, o null."],
          ]}
        />

        <H4>Ejemplos</H4>
        <CodeTabs tabs={getRequestTabs("/api/v1/stock/historial", "?desde=2026-07-01&limit=50")} />
        <H4>Respuesta 200</H4>
        <Pre>{`{
  "movimientos": [
    {
      "id": 8821,
      "producto_id": 12345,
      "producto_nombre": "Jeringa 5ml",
      "ref_proveedor": "JER-5ML",
      "tipo": "entrada",
      "cantidad_anterior": 40,
      "cantidad_nueva": 140,
      "delta": 100,
      "motivo": "[API] Entrada externa (ref:REF-2026-001234)",
      "metodo": "api",
      "fecha": "2026-05-22T09:15:32.000Z",
      "lote_numero": "L240115A",
      "fecha_vencimiento": "2026-12-31"
    }
  ],
  "limit": 100
}`}</Pre>
        <p style={styles.p}>
          El campo <code style={styles.codeInline}>limit</code> de la respuesta devuelve el límite
          efectivamente aplicado, no el que mandaste. Útil para detectar que te lo recortamos a 500.
        </p>
      </EndpointCard>
    </Section>
  );
}

function EndpointCatalogo() {
  return (
    <Section id="get-catalogo" title="Catálogo de productos">
      <EndpointCard
        method="GET"
        path="/api/v1/stock/catalogo"
        summary="Productos visibles que YA tienen una fila de stock para tu cliente. Sin query params."
      >
        <Callout type="warning" icon={<AlertTriangle size={16} />}>
          <strong>Problema del huevo y la gallina.</strong> Este endpoint solo devuelve productos que
          ya están en <strong>tu</strong> stock (cruce con tu tabla de stock) y con{" "}
          <code style={styles.codeInline}>visible = 1</code>. No incluye el catálogo de tu matriz ni
          el catálogo global. Es decir: <strong>no puedes descubrir aquí el{" "}
          <code style={styles.codeInline}>producto_id</code> de un producto del que todavía no tienes
          stock</strong>. Para la primera entrada de una referencia nueva, obtén el{" "}
          <code style={styles.codeInline}>producto_id</code> del panel web o de{" "}
          <a href="#get-barcode" style={styles.link}>producto-por-codigo</a>, que sí es global.
        </Callout>

        <H4>Campos devueltos</H4>
        <Table
          headers={["Campo", "Descripción"]}
          rows={[
            ["producto_id", "ID a usar en entradas y salidas."],
            ["nombre", "Nombre del producto."],
            ["ref_proveedor", "Referencia del proveedor."],
            ["unidadbasica", "Texto descriptivo del embalaje."],
            ["cantidad_embalaje", "Unidades por embalaje."],
          ]}
        />

        <H4>Ejemplos</H4>
        <CodeTabs tabs={getRequestTabs("/api/v1/stock/catalogo")} />
        <H4>Respuesta 200</H4>
        <Pre>{`{
  "productos": [
    {
      "producto_id": 12345,
      "nombre": "Jeringa 5ml",
      "ref_proveedor": "JER-5ML",
      "unidadbasica": "1 caja de 100",
      "cantidad_embalaje": 100
    }
  ]
}`}</Pre>
        <p style={styles.p}>
          Sin paginación ni tope: devuelve el catálogo entero. Ordenado por nombre.
        </p>
      </EndpointCard>
    </Section>
  );
}

function EndpointBarcode() {
  return (
    <Section id="get-barcode" title="Resolver código de barras">
      <EndpointCard
        method="GET"
        path="/api/v1/stock/producto-por-codigo/:codigo"
        summary="Convierte un código de barras en producto + factor de unidades. Pensado para pistolas y lectores físicos antes de llamar a entradas o salidas."
      >
        <H4>Path params</H4>
        <Table
          headers={["Param", "Tipo", "Descripción"]}
          rows={[["codigo", "string", "El código leído, tal cual. Se le hace trim."]]}
        />

        <Callout type="warning" icon={<AlertTriangle size={16} />}>
          <strong>Es un lookup GLOBAL.</strong> Busca el código en toda la tabla de códigos de
          barras de CentralSalud, <strong>sin filtrar por cliente</strong>. Resolverá
          cualquier producto de la plataforma aunque no lo tengas en tu stock ni lo hayas comprado
          nunca. Devuelve solo la ficha del producto (nunca datos de otro cliente), pero es la vía
          práctica para descubrir un <code style={styles.codeInline}>producto_id</code> que{" "}
          <a href="#get-catalogo" style={styles.link}>/catalogo</a> todavía no te muestra.
        </Callout>

        <H4>Campos devueltos</H4>
        <Table
          headers={["Campo", "Descripción"]}
          rows={[
            ["codigo", "El código encontrado."],
            ["tipo", 'Normalmente "caja" o "unidad".'],
            ["factor_unidades", "Unidades que representa una lectura de ese código."],
            ["producto_id", "ID a usar en entradas y salidas."],
            ["nombre", "Nombre del producto."],
            ["ref_proveedor", "Referencia del proveedor."],
            ["unidadbasica", "Texto descriptivo del embalaje."],
          ]}
        />

        <H4>Ejemplos</H4>
        <CodeTabs tabs={getRequestTabs("/api/v1/stock/producto-por-codigo/8412345678901")} />
        <H4>Respuesta 200</H4>
        <Pre>{`{
  "codigo": "8412345678901",
  "tipo": "caja",
  "factor_unidades": 100,
  "producto_id": 12345,
  "nombre": "Jeringa 5ml",
  "ref_proveedor": "JER-5ML",
  "unidadbasica": "1 caja de 100"
}`}</Pre>
        <Callout type="info" icon={<Key size={14} />}>
          Si <code style={styles.codeInline}>tipo</code> es{" "}
          <code style={styles.codeInline}>"caja"</code>,{" "}
          <code style={styles.codeInline}>factor_unidades</code> indica cuántas unidades trae la
          caja: multiplica la lectura por ese factor antes de enviar{" "}
          <code style={styles.codeInline}>cantidad_unidades</code>. Si es{" "}
          <code style={styles.codeInline}>"unidad"</code>, suele ser 1. La API{" "}
          <strong>no aplica el factor por ti</strong>:{" "}
          <code style={styles.codeInline}>/entradas</code> y{" "}
          <code style={styles.codeInline}>/salidas</code> siempre esperan unidades sueltas.
        </Callout>
        <p style={styles.p}>
          Si el código no está asociado a ningún producto, devuelve{" "}
          <code style={styles.codeInline}>404 barcode_not_found</code>.
        </p>
      </EndpointCard>
    </Section>
  );
}


// ─── Datasets BI ───────────────────────────────────────────────────────────
function BiIntro() {
  return (
    <Section id="bi-datasets" title="API 2 · Datos (BI): cómo funciona">
      <p style={styles.p}>
        A partir de aquí empieza la <strong>segunda API</strong>. Todo lo anterior describía la API
        de Stock; esta sección cubre <code style={styles.codeInline}>/api/v1/bi/*</code>, que usa la
        misma credencial pero cambia el alcance (toda la red), la paginación (keyset) y el formato
        de respuesta (envoltura <code style={styles.codeInline}>data / count / next_cursor</code>).
      </p>
      <p style={styles.p}>
        Son <strong>diez datasets de solo lectura</strong> pensados para alimentar herramientas de
        Business Intelligence: Power BI, Excel / Power Query, Looker Studio, Metabase o tu propio
        data warehouse. Devuelven tablas planas (una fila = un hecho), sin agregaciones, para que las
        modeles tú. Todos comparten token, paginación y envoltura de respuesta.
      </p>

      <H3>Los diez datasets</H3>
      <Table
        headers={["Endpoint", "Qué contiene", "Servicio de stock", "desde / hasta"]}
        rows={[
          [
            "GET /v1/bi/ventas-lineas",
            "Una fila = una línea de pedido, con importes, IVA, producto, proveedor y cliente.",
            "No",
            "fecha del pedido",
          ],
          [
            "GET /v1/bi/pedidos",
            "Una fila = una cabecera de pedido, con totales, estado, albarán y destino.",
            "No",
            "fecha",
          ],
          [
            "GET /v1/bi/estados-pedido",
            "Una fila = un cambio de estado. Lead time y tiempos de entrega.",
            "No",
            "fecha del cambio",
          ],
          [
            "GET /v1/bi/facturacion",
            "Proyección económica de los pedidos con importe: base, IVA y total.",
            "No",
            "fecha",
          ],
          [
            "GET /v1/bi/inventario",
            "Existencias actuales de la red, un registro por producto y clínica.",
            "Sí (403 si no)",
            "se ignoran",
          ],
          [
            "GET /v1/bi/lotes",
            "Lotes con su fecha de vencimiento y las unidades que quedan.",
            "Sí (403 si no)",
            "fecha_vencimiento",
          ],
          [
            "GET /v1/bi/movimientos",
            "Entradas, salidas, ajustes y cargas iniciales de stock.",
            "Sí (403 si no)",
            "fecha del movimiento",
          ],
          [
            "GET /v1/bi/catalogo",
            "Ficha de los productos presentes en el stock de la red: familia, marca, IVA.",
            "Sí (403 si no)",
            "se ignoran",
          ],
          [
            "GET /v1/bi/clinicas",
            "Dimensión de centros: razón social, ubicación, matriz y servicios activos.",
            "No",
            "se ignoran",
          ],
          [
            "GET /v1/bi/proveedores",
            "Dimensión de proveedores con los que la red ha hecho pedidos.",
            "No",
            "se ignoran",
          ],
        ]}
      />
      <p style={styles.p}>
        Un modelo estrella típico usa{" "}
        <code style={styles.codeInline}>ventas-lineas</code> como tabla de hechos y{" "}
        <code style={styles.codeInline}>clinicas</code>,{" "}
        <code style={styles.codeInline}>proveedores</code> y{" "}
        <code style={styles.codeInline}>catalogo</code> como dimensiones, unidas por{" "}
        <code style={styles.codeInline}>cliente_id</code>,{" "}
        <code style={styles.codeInline}>proveedor_id</code> y{" "}
        <code style={styles.codeInline}>producto_id</code>.
      </p>

      <Callout type="warning" icon={<Network size={16} />}>
        <strong>Alcance de red.</strong> A diferencia de los endpoints de stock, los datasets BI
        devuelven datos del <strong>cliente del token MÁS todas sus clínicas hijas</strong>. Filtra
        por <code style={styles.codeInline}>cliente_id</code> en tu modelo si quieres una clínica
        concreta.
      </Callout>

      <H3>Datasets de stock: requieren el servicio contratado</H3>
      <Callout type="danger" icon={<Shield size={16} />}>
        <strong>La gestión de stock es un servicio de pago.</strong> Los cuatro datasets que exponen
        existencias —<code style={styles.codeInline}>inventario</code>,{" "}
        <code style={styles.codeInline}>lotes</code>,{" "}
        <code style={styles.codeInline}>movimientos</code> y{" "}
        <code style={styles.codeInline}>catalogo</code>— exigen tener{" "}
        <code style={styles.codeInline}>gestion_stock</code> activa (en cualquiera de sus
        modalidades: albarán, picking o mixto). Si está en{" "}
        <code style={styles.codeInline}>inactivo</code>, responden{" "}
        <code style={styles.codeInline}>403 stock_disabled</code>. Los otros seis datasets funcionan
        siempre, con servicio o sin él.
      </Callout>
      <Callout type="warning" icon={<AlertTriangle size={16} />}>
        <strong>El flag se comprueba sobre el cliente DUEÑO DEL TOKEN, no sobre cada clínica de la
        red.</strong> Es una comprobación única al entrar, no un filtro fila a fila. La consecuencia
        práctica sorprende: si una clínica hija tiene el servicio contratado pero el token se emitió
        a una matriz que no lo tiene, esos cuatro datasets devuelven{" "}
        <code style={styles.codeInline}>403</code> y no verás{" "}
        <strong>ni siquiera el stock de la hija que sí paga</strong>. La solución no es contratar más
        servicios, sino <strong>emitir el token a la clínica que tiene el servicio activo</strong>.
        Al revés funciona igual de tajante: si la matriz lo tiene, la respuesta incluye el stock de
        toda la red aunque alguna hija no lo tenga.
      </Callout>
      <p style={styles.p}>
        Si tu proceso de carga mezcla datasets premium y abiertos,{" "}
        <strong>trata el <code style={styles.codeInline}>403 stock_disabled</code> como una condición
        esperada</strong> y no como un fallo: registra un aviso, salta ese dataset y sigue con el
        resto. Un ETL que aborte entero por ese 403 deja sin actualizar informes de compras que no
        dependen del stock para nada.
      </p>

      <Callout type="warning" icon={<AlertTriangle size={16} />}>
        <strong><code style={styles.codeInline}>desde</code> y{" "}
        <code style={styles.codeInline}>hasta</code> se ignoran en cuatro datasets:</strong>{" "}
        <code style={styles.codeInline}>inventario</code>,{" "}
        <code style={styles.codeInline}>catalogo</code>,{" "}
        <code style={styles.codeInline}>clinicas</code> y{" "}
        <code style={styles.codeInline}>proveedores</code>. No son series temporales: los dos
        primeros son una <strong>foto del estado actual</strong> y los dos últimos, dimensiones. Si
        los mandas, no da error ni advertencia: los parámetros simplemente{" "}
        <strong>no se aplican</strong> y recibes el dataset completo. No los uses para reconstruir el
        histórico de existencias de un mes cerrado; para eso está{" "}
        <a href="#get-bi-movimientos" style={styles.link}>movimientos</a>, que sí es una serie
        temporal.
      </Callout>

      <Callout type="danger" icon={<AlertTriangle size={16} />}>
        <strong>Trampa de nombres: <code style={styles.codeInline}>cantidad</code> NO significa lo
        mismo en todos los datasets.</strong> El nombre del campo se repite, el concepto de negocio
        no. Sumar o cruzar estas columnas entre datasets produce cifras sin ningún sentido, y lo peor
        es que el informe no falla: enseña un número creíble y equivocado.
      </Callout>
      <Table
        headers={["Dataset", "Qué mide cantidad", "Unidad de negocio"]}
        rows={[
          [
            "ventas-lineas",
            "Las unidades PEDIDAS en esa línea de pedido.",
            "Un hecho de compra en el momento del pedido. Es histórico y no cambia.",
          ],
          [
            "inventario",
            "Las existencias ACTUALES de ese producto en esa clínica.",
            "Un saldo vivo. Cambia con cada entrada o salida.",
          ],
          [
            "lotes",
            "Las existencias que quedan EN ESE LOTE concreto.",
            "Un saldo vivo desglosado. La suma de los lotes de un producto tiende al inventario de ese producto.",
          ],
        ]}
      />
      <p style={styles.p}>
        Regla práctica: <strong>renombra la columna al importarla</strong> (
        <code style={styles.codeInline}>unidades_pedidas</code>,{" "}
        <code style={styles.codeInline}>existencias</code>,{" "}
        <code style={styles.codeInline}>existencias_lote</code>) antes de modelar. En{" "}
        <a href="#get-bi-movimientos" style={styles.link}>movimientos</a> el problema no se da porque
        ahí no hay <code style={styles.codeInline}>cantidad</code>: hay{" "}
        <code style={styles.codeInline}>cantidad_anterior</code>,{" "}
        <code style={styles.codeInline}>cantidad_nueva</code> y{" "}
        <code style={styles.codeInline}>delta</code>.
      </p>

      <H3>Query params comunes</H3>
      <Table
        headers={["Param", "Tipo", "Req", "Descripción"]}
        rows={[
          [
            "desde",
            "string",
            "no",
            "Fecha inicial inclusiva. TIENE que ser exactamente YYYY-MM-DD. El campo sobre el que actúa cambia según el dataset (ver la tabla resumen). Cuatro datasets lo ignoran.",
          ],
          [
            "hasta",
            "string",
            "no",
            "Fecha final inclusiva. TIENE que ser exactamente YYYY-MM-DD. Mismas salvedades que desde.",
          ],
          [
            "cursor",
            "integer",
            "no",
            "Puntero de paginación keyset. Por defecto 0 (desde el principio). Valores negativos se normalizan a 0.",
          ],
          [
            "limit",
            "integer",
            "no",
            "Filas por página. Por defecto 500, mínimo 1, tope 1000. Valores no numéricos caen a 500 y los mayores de 1000 se recortan.",
          ],
        ]}
      />

      <Callout type="danger" icon={<AlertTriangle size={16} />}>
        <strong>Las fechas mal formadas se ignoran EN SILENCIO.</strong>{" "}
        <code style={styles.codeInline}>desde</code> y{" "}
        <code style={styles.codeInline}>hasta</code> se validan contra{" "}
        <code style={styles.codeInline}>^\d{"{4}"}-\d{"{2}"}-\d{"{2}"}$</code>. Si no encajan, el
        filtro <strong>se descarta sin devolver ningún error</strong>: recibes un{" "}
        <code style={styles.codeInline}>200</code> con el dataset completo, no con el rango que
        pediste. <code style={styles.codeInline}>2026-7-1</code>,{" "}
        <code style={styles.codeInline}>01/07/2026</code> o{" "}
        <code style={styles.codeInline}>2026-07-01T00:00:00Z</code> son todos inválidos. Si tu
        informe muestra más datos de los esperados, empieza revisando esto.
      </Callout>

      <H3>Forma de la respuesta</H3>
      <p style={styles.p}>Los diez datasets devuelven exactamente la misma envoltura:</p>
      <Pre>{`{
  "data": [ /* array de filas */ ],
  "count": 500,          // = data.length
  "next_cursor": 84213   // o null si no hay más páginas
}`}</Pre>

      <H3>Paginación keyset</H3>
      <p style={styles.p}>
        No usamos <code style={styles.codeInline}>OFFSET</code>: paginamos por clave. Cada respuesta
        te devuelve el <code style={styles.codeInline}>next_cursor</code> a pasar en la siguiente
        llamada. Las filas vienen siempre ordenadas de forma ascendente por esa clave.
      </p>
      <Table
        headers={["Regla", "Detalle"]}
        rows={[
          [
            "Cuándo viene poblado",
            "next_cursor solo trae valor si data.length === limit, es decir, si la página vino llena. En ese caso es el id de la última fila devuelta.",
          ],
          [
            "Cuándo es null",
            "Si la página vino incompleta (data.length < limit), asumimos que no hay más y devolvemos null. null = fin del dataset: debes detenerte ahí.",
          ],
          [
            "Falso positivo",
            "Si el dataset tiene un múltiplo exacto de limit, la última página llena devuelve un cursor no nulo y la siguiente llamada trae data vacía con next_cursor null. Es normal: una llamada de más, sin duplicados.",
          ],
          [
            "Estabilidad",
            "Al paginar por id ascendente, las filas nuevas se insertan al final y nunca desplazan lo ya leído. No hay ni saltos ni duplicados a mitad de la descarga.",
          ],
        ]}
      />

      <H4>Bucle de paginación</H4>
      <CodeTabs
        tabs={[
          {
            label: "curl",
            code: `# Página 1: sin cursor (equivale a cursor=0)
curl "${BASE_URL}/api/v1/bi/ventas-lineas?desde=2026-01-01&limit=1000" \\
  -H "Authorization: Bearer csk_live_xxx..."
# -> { "data": [...], "count": 1000, "next_cursor": 84213 }

# Página 2: se pasa el next_cursor recibido
curl "${BASE_URL}/api/v1/bi/ventas-lineas?desde=2026-01-01&limit=1000&cursor=84213" \\
  -H "Authorization: Bearer csk_live_xxx..."
# -> { "data": [...], "count": 412, "next_cursor": null }   <- fin`,
          },
          {
            label: "Node.js",
            code: `const BASE = '${BASE_URL}';
const TOKEN = process.env.CSK_TOKEN;

async function descargarTodo(dataset, filtros = {}) {
  const filas = [];
  let cursor = 0;

  while (cursor !== null) {
    const qs = new URLSearchParams({ ...filtros, limit: '1000', cursor: String(cursor) });
    const r = await fetch(BASE + '/api/v1/bi/' + dataset + '?' + qs, {
      headers: { Authorization: 'Bearer ' + TOKEN },
    });
    if (!r.ok) throw new Error('HTTP ' + r.status + ': ' + await r.text());

    const json = await r.json();
    filas.push(...json.data);
    cursor = json.next_cursor;   // null corta el bucle
  }
  return filas;
}

const ventas = await descargarTodo('ventas-lineas', { desde: '2026-01-01' });
console.log(ventas.length, 'lineas');`,
          },
          {
            label: "Python",
            code: `import os, requests

BASE  = "${BASE_URL}"
TOKEN = os.environ["CSK_TOKEN"]

def descargar_todo(dataset, **filtros):
    filas, cursor = [], 0
    while cursor is not None:
        r = requests.get(
            f"{BASE}/api/v1/bi/{dataset}",
            params={**filtros, "limit": 1000, "cursor": cursor},
            headers={"Authorization": f"Bearer {TOKEN}"},
            timeout=60,
        )
        r.raise_for_status()
        payload = r.json()
        filas.extend(payload["data"])
        cursor = payload["next_cursor"]   # None corta el bucle
    return filas

ventas = descargar_todo("ventas-lineas", desde="2026-01-01")
print(len(ventas), "lineas")`,
          },
          {
            label: "PHP",
            code: `<?php
$base  = '${BASE_URL}';
$token = getenv('CSK_TOKEN');
$filas = [];
$cursor = 0;

while ($cursor !== null) {
    $qs = http_build_query([
        'desde'  => '2026-01-01',
        'limit'  => 1000,
        'cursor' => $cursor,
    ]);
    $ch = curl_init($base . '/api/v1/bi/ventas-lineas?' . $qs);
    curl_setopt_array($ch, [
        CURLOPT_RETURNTRANSFER => true,
        CURLOPT_HTTPHEADER     => ['Authorization: Bearer ' . $token],
    ]);
    $payload = json_decode(curl_exec($ch), true);
    curl_close($ch);

    $filas  = array_merge($filas, $payload['data']);
    $cursor = $payload['next_cursor'];   // null corta el bucle
}
echo count($filas) . " lineas\\n";`,
          },
        ]}
      />

      <H3>Diccionario de estados de pedido</H3>
      <p style={styles.p}>
        Los campos <code style={styles.codeInline}>estado</code> (en ventas) y{" "}
        <code style={styles.codeInline}>estado_anterior</code> /{" "}
        <code style={styles.codeInline}>estado_nuevo</code> (en estados de pedido) son enteros.
        Este es el mapa completo:
      </p>
      <Table
        headers={["Código", "Estado", "Significado"]}
        rows={[
          ["1", "Abierto", "Pedido creado, todavía no enviado al proveedor."],
          ["2", "En proceso", "Enviado al proveedor y en curso."],
          ["3", "Recibido", "Recibido completo."],
          ["4", "Recibido parcial", "Llegó parte de la mercancía; el resto sigue pendiente."],
          ["5", "Cerrado", "Cerrado definitivamente."],
          ["6", "Pendiente de aprobación", "Esperando autorización interna antes de enviarse."],
          ["7", "Rechazado", "No aprobado; no llega a enviarse."],
        ]}
      />
      <p style={styles.p}>
        En <code style={styles.codeInline}>estados-pedido</code>,{" "}
        <code style={styles.codeInline}>estado_anterior</code> puede venir{" "}
        <code style={styles.codeInline}>null</code> en la primera transición del pedido (creación).
      </p>
    </Section>
  );
}

function EndpointBiVentas() {
  return (
    <Section id="get-bi-ventas" title="Ventas por línea">
      <EndpointCard
        method="GET"
        path="/api/v1/bi/ventas-lineas"
        summary="Tabla de hechos de ventas: una fila por línea de pedido, con producto, proveedor, cliente e importes ya calculados."
      >
        <H4>Query params</H4>
        <Table
          headers={["Param", "Tipo", "Descripción"]}
          rows={[
            ["desde", "string", "YYYY-MM-DD. Filtra por FECHA DEL PEDIDO. Formato inválido = filtro ignorado en silencio."],
            ["hasta", "string", "YYYY-MM-DD. Filtra por FECHA DEL PEDIDO. Se compara SIN normalizar (lee el aviso)."],
            ["cursor", "integer", "Keyset sobre linea_id. Por defecto 0."],
            ["limit", "integer", "Por defecto 500, tope 1000."],
          ]}
        />

        <Callout type="warning" icon={<AlertTriangle size={16} />}>
          <strong>Diferencia de comportamiento entre datasets BI.</strong> Aquí{" "}
          <code style={styles.codeInline}>hasta</code> se compara{" "}
          <strong>tal cual, sin añadir la hora</strong>. En{" "}
          <a href="#get-bi-estados" style={styles.link}>estados-pedido</a> sí se normaliza a{" "}
          <code style={styles.codeInline}>23:59:59</code>. Si el campo de fecha del pedido guardase
          hora, <code style={styles.codeInline}>hasta=2026-07-20</code> dejaría fuera ese día; si es
          fecha pura, lo incluye. Para no depender de eso,{" "}
          <strong>pide siempre un día más y recorta en tu modelo</strong>. (El endpoint{" "}
          <a href="#get-historial" style={styles.link}>/stock/historial</a> tampoco normaliza y
          además no valida el formato: son tres comportamientos distintos; tenlo en cuenta al
          portar código entre ellos.)
        </Callout>

        <H4>Campos devueltos</H4>
        <Table
          headers={["Campo", "Tipo", "Descripción"]}
          rows={[
            ["linea_id", "integer", "ID de la línea. Es la CLAVE DE PAGINACIÓN y la clave única de la fila."],
            ["pedido_id", "integer", "ID interno del pedido al que pertenece la línea."],
            ["npedido", "string", "Número de pedido visible en el panel."],
            ["fecha_pedido", "date", "Fecha del pedido. Es el campo sobre el que actúan desde y hasta."],
            ["fecha_cierre", "date", "Fecha de cierre del pedido, o null si sigue abierto."],
            ["estado", "integer", "Estado ACTUAL del pedido (1-7). Ver el diccionario de estados."],
            ["tipo", "string", "Tipo de pedido."],
            ["cliente_id", "integer", "Clínica que hizo el pedido. Clave para desglosar por centro dentro de la red."],
            ["cliente_nombre", "string", "Razón social de esa clínica."],
            ["proveedor_id", "integer", "Proveedor del pedido, o null."],
            ["proveedor_nombre", "string", "Nombre del proveedor, o null."],
            ["producto_id", "integer", "Producto de la línea."],
            ["producto_nombre", "string", "Nombre del producto tal como quedó CONGELADO en la línea del pedido (puede diferir del nombre actual del producto)."],
            ["ref_proveedor", "string", "Referencia actual del producto, o null si el producto ya no existe."],
            ["cantidad", "number", "Unidades pedidas en la línea."],
            ["precio", "number", "Precio unitario sin IVA."],
            ["iva", "number", "Porcentaje de IVA aplicado, por ejemplo 21."],
            ["total_linea", "number", "precio × cantidad, redondeado a 4 decimales. Sin IVA."],
            ["total_linea_iva", "number", "precio × cantidad × (1 + iva/100), redondeado a 4 decimales. Con IVA."],
          ]}
        />
        <p style={styles.p}>
          <code style={styles.codeInline}>total_linea</code> y{" "}
          <code style={styles.codeInline}>total_linea_iva</code> vienen precalculados: súmalos
          directamente en tu herramienta sin recalcular, así los redondeos cuadran con los del panel.
        </p>

        <H4>Ejemplos</H4>
        <CodeTabs
          tabs={getRequestTabs(
            "/api/v1/bi/ventas-lineas",
            "?desde=2026-01-01&hasta=2026-06-30&limit=1000"
          )}
        />

        <H4>Respuesta 200</H4>
        <Pre>{`{
  "data": [
    {
      "linea_id": 84213,
      "pedido_id": 9912,
      "npedido": "P-2026-000341",
      "fecha_pedido": "2026-03-14",
      "fecha_cierre": "2026-03-21",
      "estado": 5,
      "tipo": "normal",
      "cliente_id": 42,
      "cliente_nombre": "Clínica Dental Norte S.L.",
      "proveedor_id": 17,
      "proveedor_nombre": "Distribuciones Médicas SA",
      "producto_id": 12345,
      "producto_nombre": "Jeringa 5ml",
      "ref_proveedor": "JER-5ML",
      "cantidad": 10,
      "precio": 3.45,
      "iva": 21,
      "total_linea": 34.5,
      "total_linea_iva": 41.745
    }
  ],
  "count": 1,
  "next_cursor": null
}`}</Pre>

        <H4>Códigos</H4>
        <Table
          headers={["Status", "Cuándo"]}
          rows={[
            ["200", "Siempre que el token sea válido. Un rango sin resultados devuelve data vacío y next_cursor null, no un 404."],
            ["401", "Token ausente, mal formado, revocado o inexistente."],
            ["500 auth_error", "Fallo al validar el token. Reinténtalo."],
            ["500 internal_error", "Error al consultar. Reinténtalo con la misma página: es idempotente."],
          ]}
        />
        <p style={styles.p}>
          Este endpoint <strong>no devuelve nunca</strong>{" "}
          <code style={styles.codeInline}>403 stock_disabled</code>.
        </p>
      </EndpointCard>
    </Section>
  );
}

function EndpointBiEstados() {
  return (
    <Section id="get-bi-estados" title="Estados de pedido">
      <EndpointCard
        method="GET"
        path="/api/v1/bi/estados-pedido"
        summary="Histórico de transiciones: una fila por cambio de estado de un pedido. Pensado para calcular lead time y tiempos de entrega."
      >
        <Callout type="warning" icon={<AlertTriangle size={16} />}>
          <strong>No todas las filas son cambios reales: mira siempre{" "}
          <code style={styles.codeInline}>origen</code>.</strong>{" "}
          Las filas con <code style={styles.codeInline}>origen = "trigger"</code> son transiciones
          REALES, capturadas en vivo en el momento en que ocurrieron. Las filas con{" "}
          <code style={styles.codeInline}>origen = "backfill"</code> son una{" "}
          <strong>reconstrucción</strong> del histórico anterior a la instalación de los
          disparadores, hecha a partir de <code style={styles.codeInline}>fecha</code> y{" "}
          <code style={styles.codeInline}>fecha_cierre</code> del pedido. De esos pedidos solo se
          conservan <strong>dos eventos: la apertura y el cierre</strong>. Los estados intermedios
          (En proceso, Recibido parcial…) <strong>no existen</strong>: no dejaron rastro y no se
          inventan. Un pedido que recorrió 1 → 2 → 4 → 5 aparece como un único salto 1 → 5. Además,
          las dos fechas de origen no llevan hora, así que esos eventos quedan a las{" "}
          <code style={styles.codeInline}>00:00:00</code> y su granularidad real es{" "}
          <strong>el día, no el minuto</strong>.
        </Callout>
        <p style={styles.p}>
          Consecuencias para tu modelo: para métricas de precisión (tiempo entre estados intermedios,
          lead times por horas) <strong>filtra{" "}
          <code style={styles.codeInline}>origen = "trigger"</code></strong> o segmenta por fecha,
          quedándote con el periodo posterior a la puesta en marcha de los disparadores. Los eventos{" "}
          <code style={styles.codeInline}>backfill</code> sí sirven para una métrica más gruesa —
          días entre apertura y cierre del pedido — siempre que no los mezcles con los reales en el
          mismo indicador. Y no interpretes la ausencia de estados intermedios como un pedido sin
          movimiento: significa que ese movimiento ocurrió antes de que existiera el registro.
        </p>

        <H4>Query params</H4>
        <Table
          headers={["Param", "Tipo", "Descripción"]}
          rows={[
            ["desde", "string", "YYYY-MM-DD. Filtra por la FECHA DEL CAMBIO, no por la del pedido. Formato inválido = filtro ignorado en silencio."],
            ["hasta", "string", "YYYY-MM-DD. Se normaliza internamente a <fecha> 23:59:59, así que el día indicado SÍ se incluye entero."],
            ["cursor", "integer", "Keyset sobre id. Por defecto 0."],
            ["limit", "integer", "Por defecto 500, tope 1000."],
          ]}
        />

        <Callout type="info" icon={<Table2 size={16} />}>
          <strong>Aquí <code style={styles.codeInline}>hasta</code> sí incluye el día completo.</strong>{" "}
          Este es el único endpoint que normaliza el límite superior a{" "}
          <code style={styles.codeInline}>{"<fecha> 23:59:59"}</code>. En{" "}
          <a href="#get-bi-ventas" style={styles.link}>ventas-lineas</a> y en{" "}
          <a href="#get-historial" style={styles.link}>/stock/historial</a> no se normaliza. No
          asumas el mismo comportamiento al reutilizar código entre datasets.
        </Callout>

        <H4>Campos devueltos</H4>
        <Table
          headers={["Campo", "Tipo", "Descripción"]}
          rows={[
            ["id", "integer", "ID de la transición. Es la CLAVE DE PAGINACIÓN."],
            ["pedido_id", "integer", "Pedido al que corresponde el cambio. Une con ventas-lineas por este campo."],
            ["npedido", "string", "Número de pedido visible en el panel."],
            ["cliente_id", "integer", "Clínica dueña del pedido."],
            ["estado_anterior", "integer", "Estado del que venía (1-7). Puede ser null en la primera transición."],
            ["estado_nuevo", "integer", "Estado al que pasó (1-7)."],
            ["cambiado_en", "datetime", "Momento del cambio. Es el campo sobre el que actúan desde y hasta. En las filas de backfill no hay hora: quedan a las 00:00:00."],
            ["origen", "string", "trigger = cambio real capturado en vivo por los disparadores. backfill = reconstruido a partir de fecha y fecha_cierre del pedido (solo apertura y cierre; sin estados intermedios)."],
          ]}
        />
        <p style={styles.p}>
          Para calcular un lead time, une por{" "}
          <code style={styles.codeInline}>pedido_id</code> y resta el{" "}
          <code style={styles.codeInline}>cambiado_en</code> de la transición a{" "}
          <code style={styles.codeInline}>estado_nuevo = 2</code> (En proceso) del de la transición a{" "}
          <code style={styles.codeInline}>estado_nuevo = 3</code> (Recibido). Un pedido puede pasar
          varias veces por el mismo estado: quédate con el primero o el último según tu definición.
        </p>

        <H4>Ejemplos</H4>
        <CodeTabs
          tabs={getRequestTabs(
            "/api/v1/bi/estados-pedido",
            "?desde=2026-01-01&hasta=2026-06-30&limit=1000"
          )}
        />

        <H4>Respuesta 200</H4>
        <Pre>{`{
  "data": [
    {
      "id": 51204,
      "pedido_id": 9912,
      "npedido": "P-2026-000341",
      "cliente_id": 42,
      "estado_anterior": 2,
      "estado_nuevo": 3,
      "cambiado_en": "2026-03-19T11:42:08.000Z",
      "origen": "trigger"
    }
  ],
  "count": 1,
  "next_cursor": null
}`}</Pre>

        <H4>Códigos</H4>
        <Table
          headers={["Status", "Cuándo"]}
          rows={[
            ["200", "Siempre que el token sea válido. Sin resultados devuelve data vacío, no un 404."],
            ["401", "Token ausente, mal formado, revocado o inexistente."],
            ["500 auth_error", "Fallo al validar el token."],
            ["500 internal_error", "Error al consultar el histórico."],
          ]}
        />
      </EndpointCard>
    </Section>
  );
}

function EndpointBiPedidos() {
  return (
    <Section id="get-bi-pedidos" title="Pedidos">
      <EndpointCard
        method="GET"
        path="/api/v1/bi/pedidos"
        summary="Cabeceras de pedido: una fila por pedido, con sus totales ya sumados. Es la tabla de hechos a nivel de documento, complementaria a ventas-lineas."
      >
        <p style={styles.p}>
          Usa este dataset cuando cuentes <strong>pedidos</strong> y no líneas: número de pedidos por
          clínica, importe medio, reparto por proveedor o por estado. Si intentas contar pedidos
          sobre <a href="#get-bi-ventas" style={styles.link}>ventas-lineas</a> tendrás que hacer un
          distinct sobre <code style={styles.codeInline}>pedido_id</code>, y cualquier descuido
          multiplica el importe por el número de líneas.
        </p>

        <H4>Query params</H4>
        <Table
          headers={["Param", "Tipo", "Descripción"]}
          rows={[
            ["desde", "string", "YYYY-MM-DD. Filtra por el campo fecha del pedido. Formato inválido = filtro ignorado en silencio."],
            ["hasta", "string", "YYYY-MM-DD. Filtra por el campo fecha del pedido."],
            ["cursor", "integer", "Keyset sobre pedido_id. Por defecto 0."],
            ["limit", "integer", "Por defecto 500, tope 1000."],
          ]}
        />

        <H4>Campos devueltos</H4>
        <Table
          headers={["Campo", "Tipo", "Descripción"]}
          rows={[
            ["pedido_id", "integer", "ID interno del pedido. Es la CLAVE DE PAGINACIÓN y la clave para unir con ventas-lineas, estados-pedido y facturacion."],
            ["npedido", "string", "Número de pedido visible en el panel. Es el identificador que reconoce el usuario, pero une siempre por pedido_id."],
            ["cliente_id", "integer", "Clínica que hizo el pedido. Une con clinicas."],
            ["cliente_nombre", "string", "Razón social de esa clínica."],
            ["proveedor_id", "integer", "Proveedor del pedido, o null. Une con proveedores."],
            ["proveedor_nombre", "string", "Nombre del proveedor, o null."],
            ["estado", "integer", "Estado ACTUAL del pedido (1-7). Ver el diccionario de estados."],
            ["tipo", "string", "Tipo de pedido."],
            ["fecha", "date", "Fecha del pedido. Es el campo sobre el que actúan desde y hasta."],
            ["fecha_cierre", "date", "Fecha de cierre, o null si el pedido sigue abierto."],
            ["base_imponible", "number", "Total del pedido sin IVA, ya sumado a partir de sus líneas."],
            ["total_con_iva", "number", "Total del pedido con IVA."],
            ["albaran", "string", "Número de albarán asociado, o null si todavía no se ha recibido."],
            ["favorito", "boolean", "Si el pedido está marcado como favorito en el panel. Es una marca de usuario, no un dato contable."],
            ["clinica_matriz_destino", "integer", "Clínica matriz de destino, o null. Útil para consolidar por grupo cuando la red tiene varios niveles."],
          ]}
        />
        <p style={styles.p}>
          <code style={styles.codeInline}>base_imponible</code> y{" "}
          <code style={styles.codeInline}>total_con_iva</code> vienen ya agregados desde las líneas.
          Si sumas los <code style={styles.codeInline}>total_linea</code> de{" "}
          <a href="#get-bi-ventas" style={styles.link}>ventas-lineas</a> deberías obtener la misma
          cifra salvo diferencias de redondeo al último céntimo:{" "}
          <strong>no mezcles ambas fuentes en el mismo indicador</strong>, elige una.
        </p>

        <H4>Ejemplos</H4>
        <CodeTabs
          tabs={getRequestTabs(
            "/api/v1/bi/pedidos",
            "?desde=2026-01-01&hasta=2026-06-30&limit=1000"
          )}
        />

        <H4>Respuesta 200</H4>
        <Pre>{`{
  "data": [
    {
      "pedido_id": 9912,
      "npedido": "P-2026-000341",
      "cliente_id": 42,
      "cliente_nombre": "Clínica Dental Norte S.L.",
      "proveedor_id": 17,
      "proveedor_nombre": "Distribuciones Médicas SA",
      "estado": 5,
      "tipo": "normal",
      "fecha": "2026-03-14",
      "fecha_cierre": "2026-03-21",
      "base_imponible": 412.8,
      "total_con_iva": 499.49,
      "albaran": "ALB-88231",
      "favorito": false,
      "clinica_matriz_destino": 7
    }
  ],
  "count": 1,
  "next_cursor": null
}`}</Pre>

        <H4>Códigos</H4>
        <Table
          headers={["Status", "Cuándo"]}
          rows={[
            ["200", "Siempre que el token sea válido. Un rango sin pedidos devuelve data vacío, no un 404."],
            ["401", "Token ausente, mal formado, revocado o inexistente."],
            ["500 auth_error", "Fallo al validar el token. Reinténtalo."],
            ["500 internal_error", "Error al consultar. Reinténtalo con la misma página: es idempotente."],
          ]}
        />
        <p style={styles.p}>
          Dataset <strong>abierto</strong>: no requiere el servicio de gestión de stock y nunca
          devuelve <code style={styles.codeInline}>403 stock_disabled</code>.
        </p>
      </EndpointCard>
    </Section>
  );
}

function EndpointBiFacturacion() {
  return (
    <Section id="get-bi-facturacion" title="Facturación">
      <EndpointCard
        method="GET"
        path="/api/v1/bi/facturacion"
        summary="Proyección económica de los pedidos: base imponible, importe de IVA y total, con las marcas de facturada y confirmada."
      >
        <Callout type="warning" icon={<AlertTriangle size={16} />}>
          <strong>CentralSalud no tiene una tabla de facturas.</strong> Este dataset{" "}
          <strong>no lee facturas reales</strong>: es la{" "}
          <strong>proyección económica de los pedidos</strong>, calculada a partir de sus importes.
          No esperes número de factura, serie, vencimiento ni estado de cobro, porque esos datos no
          existen en la plataforma. Si necesitas contabilidad formal, la fuente es tu ERP; esto sirve
          para analizar <em>gasto</em>, no para cuadrar el libro de IVA.
        </Callout>

        <H4>Qué filas incluye</H4>
        <ul style={styles.ul}>
          <li>
            Solo pedidos <strong>con importe</strong>:{" "}
            <code style={styles.codeInline}>base_imponible {">"} 0</code>. Los pedidos a cero (
            anulados, de prueba o sin precios cargados) <strong>no aparecen</strong>, así que el
            número de filas de aquí es menor que el de{" "}
            <a href="#get-bi-pedidos" style={styles.link}>pedidos</a>. Es intencional: no es una
            pérdida de datos.
          </li>
          <li>
            Solo pedidos con <strong>fecha a partir de 2020</strong>. Lo anterior queda fuera por
            calidad del dato histórico. Si pides{" "}
            <code style={styles.codeInline}>desde=2018-01-01</code> no obtendrás nada anterior a
            2020, y tampoco recibirás ningún aviso.
          </li>
        </ul>

        <H4>Query params</H4>
        <Table
          headers={["Param", "Tipo", "Descripción"]}
          rows={[
            ["desde", "string", "YYYY-MM-DD. Filtra por el campo fecha del pedido. Formato inválido = filtro ignorado en silencio."],
            ["hasta", "string", "YYYY-MM-DD. Filtra por el campo fecha del pedido."],
            ["cursor", "integer", "Keyset sobre pedido_id. Por defecto 0."],
            ["limit", "integer", "Por defecto 500, tope 1000."],
          ]}
        />

        <H4>Campos devueltos</H4>
        <Table
          headers={["Campo", "Tipo", "Descripción"]}
          rows={[
            ["pedido_id", "integer", "Pedido del que sale la proyección. Es la CLAVE DE PAGINACIÓN y la clave de unión con pedidos y ventas-lineas."],
            ["npedido", "string", "Número de pedido visible en el panel."],
            ["cliente_id", "integer", "Clínica a la que se imputa el gasto."],
            ["cliente_nombre", "string", "Razón social de esa clínica."],
            ["proveedor_id", "integer", "Proveedor del pedido, o null."],
            ["fecha", "date", "Fecha del pedido. Es el campo sobre el que actúan desde y hasta."],
            ["fecha_cierre", "date", "Fecha de cierre, o null si sigue abierto."],
            ["base_imponible", "number", "Importe sin IVA. Siempre > 0 por definición del dataset."],
            ["total_con_iva", "number", "Importe con IVA."],
            ["iva_importe", "number", "Cuota de IVA: total_con_iva menos base_imponible, ya calculada."],
            ["facturada", "boolean", "Marca de facturado en el panel. Es un estado operativo del pedido, no la existencia de una factura."],
            ["confirmada", "boolean", "Marca de confirmación en el panel."],
          ]}
        />
        <p style={styles.p}>
          Un pedido con <code style={styles.codeInline}>facturada = false</code>{" "}
          <strong>sigue contando como gasto</strong> en este dataset: la marca indica en qué punto
          del circuito administrativo está, no si el importe es real. Decide explícitamente si tu
          informe de gasto filtra por esa marca o no, y documéntalo, porque las dos lecturas son
          defendibles y dan cifras distintas.
        </p>

        <H4>Ejemplos</H4>
        <CodeTabs
          tabs={getRequestTabs(
            "/api/v1/bi/facturacion",
            "?desde=2026-01-01&hasta=2026-06-30&limit=1000"
          )}
        />

        <H4>Respuesta 200</H4>
        <Pre>{`{
  "data": [
    {
      "pedido_id": 9912,
      "npedido": "P-2026-000341",
      "cliente_id": 42,
      "cliente_nombre": "Clínica Dental Norte S.L.",
      "proveedor_id": 17,
      "fecha": "2026-03-14",
      "fecha_cierre": "2026-03-21",
      "base_imponible": 412.8,
      "total_con_iva": 499.49,
      "iva_importe": 86.69,
      "facturada": true,
      "confirmada": true
    }
  ],
  "count": 1,
  "next_cursor": null
}`}</Pre>

        <H4>Códigos</H4>
        <Table
          headers={["Status", "Cuándo"]}
          rows={[
            ["200", "Siempre que el token sea válido. Sin pedidos con importe en el rango, data vacío."],
            ["401", "Token ausente, mal formado, revocado o inexistente."],
            ["500 auth_error", "Fallo al validar el token."],
            ["500 internal_error", "Error al consultar."],
          ]}
        />
        <p style={styles.p}>
          Dataset <strong>abierto</strong>: no requiere el servicio de gestión de stock.
        </p>
      </EndpointCard>
    </Section>
  );
}

function EndpointBiInventario() {
  return (
    <Section id="get-bi-inventario" title="Inventario (BI)">
      <EndpointCard
        method="GET"
        path="/api/v1/bi/inventario"
        summary="Existencias actuales de toda la red: una fila por producto y clínica, con sus umbrales y la marca de bajo mínimo ya calculada."
      >
        <Callout type="danger" icon={<Shield size={16} />}>
          <strong>Requiere el servicio de gestión de stock.</strong> Si el cliente{" "}
          <strong>dueño del token</strong> tiene{" "}
          <code style={styles.codeInline}>gestion_stock</code> en{" "}
          <code style={styles.codeInline}>inactivo</code>, este dataset responde{" "}
          <code style={styles.codeInline}>403 stock_disabled</code>.
        </Callout>

        <p style={styles.p}>
          No lo confundas con{" "}
          <a href="#get-inventario" style={styles.link}>/api/v1/stock/inventario</a>. Aquel devuelve
          el inventario de <strong>una sola clínica</strong> sin paginar; este devuelve el de{" "}
          <strong>toda la red</strong>, paginado y con{" "}
          <code style={styles.codeInline}>cliente_id</code> en cada fila para que puedas desglosar
          por centro.
        </p>

        <H4>Query params</H4>
        <Table
          headers={["Param", "Tipo", "Descripción"]}
          rows={[
            ["cursor", "integer", "Keyset sobre stock_id. Por defecto 0."],
            ["limit", "integer", "Por defecto 500, tope 1000."],
          ]}
        />

        <Callout type="warning" icon={<AlertTriangle size={16} />}>
          <strong>Es una foto del estado ACTUAL.</strong> No acepta{" "}
          <code style={styles.codeInline}>desde</code> ni{" "}
          <code style={styles.codeInline}>hasta</code>: si los mandas, se ignoran sin error. No hay
          forma de pedir "el inventario a 31 de diciembre". Para reconstruir existencias pasadas,
          parte de esta foto y aplica hacia atrás los{" "}
          <a href="#get-bi-movimientos" style={styles.link}>movimientos</a>, o guarda tú un snapshot
          diario en tu almacén.
        </Callout>

        <H4>Campos devueltos</H4>
        <Table
          headers={["Campo", "Tipo", "Descripción"]}
          rows={[
            ["stock_id", "integer", "ID de la fila de stock. Es la CLAVE DE PAGINACIÓN y la clave única de la combinación producto + clínica."],
            ["cliente_id", "integer", "Clínica dueña de esas existencias. Une con clinicas."],
            ["producto_id", "integer", "Producto. Une con catalogo."],
            ["producto_nombre", "string", "Nombre actual del producto."],
            ["ref_proveedor", "string", "Referencia del proveedor."],
            ["unidadbasica", "string", 'Texto descriptivo del embalaje, por ejemplo "1 caja de 100".'],
            ["cantidad", "number", "EXISTENCIAS ACTUALES de ese producto en esa clínica. Ojo: no es una cantidad pedida (lee la trampa de nombres)."],
            ["minimo", "number", "Umbral de bajo mínimo. 0 = sin umbral definido."],
            ["maximo", "number", "Umbral máximo. 0 = sin umbral definido."],
            ["actualizado_en", "datetime", "Último momento en que cambió esta fila de stock."],
            ["bajo_minimo", "boolean", "Campo CALCULADO por nosotros: true si cantidad < minimo Y minimo > 0."],
          ]}
        />

        <Callout type="info" icon={<Table2 size={16} />}>
          <strong><code style={styles.codeInline}>bajo_minimo</code> viene ya resuelto, pero exige{" "}
          <code style={styles.codeInline}>minimo {">"} 0</code>.</strong> Un producto con{" "}
          <code style={styles.codeInline}>minimo = 0</code> devuelve{" "}
          <code style={styles.codeInline}>false</code>{" "}
          <strong>aunque esté a cero unidades</strong>: sin umbral definido no hay nada que cruzar.
          Si tu cuadro de mando cuenta roturas de stock, ten presente que los productos sin mínimo
          configurado <strong>nunca se contarán</strong>. Para detectarlos, filtra aparte por{" "}
          <code style={styles.codeInline}>cantidad = 0</code>.
        </Callout>

        <H4>Ejemplos</H4>
        <CodeTabs tabs={getRequestTabs("/api/v1/bi/inventario", "?limit=1000")} />

        <H4>Respuesta 200</H4>
        <Pre>{`{
  "data": [
    {
      "stock_id": 30514,
      "cliente_id": 42,
      "producto_id": 12345,
      "producto_nombre": "Jeringa 5ml",
      "ref_proveedor": "JER-5ML",
      "unidadbasica": "1 caja de 100",
      "cantidad": 135,
      "minimo": 50,
      "maximo": 500,
      "actualizado_en": "2026-07-18T08:22:41.000Z",
      "bajo_minimo": false
    }
  ],
  "count": 1,
  "next_cursor": null
}`}</Pre>

        <H4>Códigos</H4>
        <Table
          headers={["Status", "Cuándo"]}
          rows={[
            ["200", "Token válido y gestión de stock activa en el cliente del token."],
            ["401", "Token ausente, mal formado, revocado o inexistente."],
            ["403 stock_disabled", "El cliente dueño del token no tiene contratada la gestión de stock."],
            ["500 auth_error", "Fallo al validar el token."],
            ["500 internal_error", "Error al consultar."],
          ]}
        />
      </EndpointCard>
    </Section>
  );
}

function EndpointBiLotes() {
  return (
    <Section id="get-bi-lotes" title="Lotes (BI)">
      <EndpointCard
        method="GET"
        path="/api/v1/bi/lotes"
        summary="Lotes de toda la red con su fecha de vencimiento y las unidades que quedan. Pensado para informes de caducidad y de inmovilizado."
      >
        <Callout type="danger" icon={<Shield size={16} />}>
          <strong>Requiere el servicio de gestión de stock.</strong> Con{" "}
          <code style={styles.codeInline}>gestion_stock</code> inactiva en el cliente del token,
          devuelve <code style={styles.codeInline}>403 stock_disabled</code>.
        </Callout>

        <H4>Query params</H4>
        <Table
          headers={["Param", "Tipo", "Descripción"]}
          rows={[
            ["desde", "string", "YYYY-MM-DD. Filtra por FECHA DE VENCIMIENTO, no por fecha de creación del lote."],
            ["hasta", "string", "YYYY-MM-DD. Filtra por FECHA DE VENCIMIENTO. Formato inválido = filtro ignorado en silencio."],
            ["cursor", "integer", "Keyset sobre lote_id. Por defecto 0."],
            ["limit", "integer", "Por defecto 500, tope 1000."],
          ]}
        />

        <Callout type="warning" icon={<AlertTriangle size={16} />}>
          <strong>Aquí <code style={styles.codeInline}>desde</code> y{" "}
          <code style={styles.codeInline}>hasta</code> actúan sobre{" "}
          <code style={styles.codeInline}>fecha_vencimiento</code>, un campo de FUTURO.</strong> Es
          el único dataset donde el rango de fechas no mira al pasado. Para "lo que caduca este
          trimestre", pide{" "}
          <code style={styles.codeInline}>desde=hoy&amp;hasta=fin_de_trimestre</code>. Y ten en
          cuenta que los lotes <strong>sin fecha de vencimiento</strong> (
          <code style={styles.codeInline}>null</code>) <strong>quedan fuera</strong> en cuanto
          aplicas cualquiera de los dos filtros: si necesitas verlos, haz la llamada sin rango.
        </Callout>

        <H4>Campos devueltos</H4>
        <Table
          headers={["Campo", "Tipo", "Descripción"]}
          rows={[
            ["lote_id", "integer", "ID interno del lote. Es la CLAVE DE PAGINACIÓN."],
            ["cliente_id", "integer", "Clínica que tiene ese lote. Une con clinicas."],
            ["producto_id", "integer", "Producto al que pertenece el lote."],
            ["producto_nombre", "string", "Nombre actual del producto."],
            ["ref_proveedor", "string", "Referencia del proveedor."],
            ["lote", "string", "Identificador del lote tal cual se registró. Puede ser API-<epoch_ms> si lo autogeneró la API de Stock."],
            ["fecha_vencimiento", "date", "Vencimiento del lote, o null si no se informó. Es el campo sobre el que actúan desde y hasta."],
            ["cantidad", "number", "Unidades que quedan EN ESE LOTE. Puede ser 0: los lotes agotados siguen apareciendo."],
            ["created_at", "datetime", "Cuándo se creó el lote."],
          ]}
        />
        <p style={styles.p}>
          <strong>Filtra tú los lotes agotados.</strong> Igual que en la API de Stock, no hay
          parámetro para excluir los que tienen{" "}
          <code style={styles.codeInline}>cantidad = 0</code>: si tu informe de caducidades no los
          descarta, contarás como riesgo mercancía que ya se consumió.
        </p>

        <H4>Ejemplos</H4>
        <CodeTabs
          tabs={getRequestTabs(
            "/api/v1/bi/lotes",
            "?desde=2026-07-01&hasta=2026-12-31&limit=1000"
          )}
        />

        <H4>Respuesta 200</H4>
        <Pre>{`{
  "data": [
    {
      "lote_id": 5891,
      "cliente_id": 42,
      "producto_id": 12345,
      "producto_nombre": "Jeringa 5ml",
      "ref_proveedor": "JER-5ML",
      "lote": "L240115A",
      "fecha_vencimiento": "2026-12-31",
      "cantidad": 100,
      "created_at": "2026-05-22T09:15:32.000Z"
    }
  ],
  "count": 1,
  "next_cursor": null
}`}</Pre>

        <H4>Códigos</H4>
        <Table
          headers={["Status", "Cuándo"]}
          rows={[
            ["200", "Token válido y gestión de stock activa en el cliente del token."],
            ["401", "Token ausente, mal formado, revocado o inexistente."],
            ["403 stock_disabled", "El cliente dueño del token no tiene contratada la gestión de stock."],
            ["500 auth_error", "Fallo al validar el token."],
            ["500 internal_error", "Error al consultar."],
          ]}
        />
      </EndpointCard>
    </Section>
  );
}

function EndpointBiMovimientos() {
  return (
    <Section id="get-bi-movimientos" title="Movimientos de stock (BI)">
      <EndpointCard
        method="GET"
        path="/api/v1/bi/movimientos"
        summary="Serie temporal de todos los movimientos de stock de la red: entradas, salidas, ajustes y cargas iniciales, con el saldo antes y después."
      >
        <Callout type="danger" icon={<Shield size={16} />}>
          <strong>Requiere el servicio de gestión de stock.</strong> Con{" "}
          <code style={styles.codeInline}>gestion_stock</code> inactiva en el cliente del token,
          devuelve <code style={styles.codeInline}>403 stock_disabled</code>.
        </Callout>

        <p style={styles.p}>
          Es el <strong>único dataset de stock que es una serie temporal</strong>. Con él se
          responden preguntas de consumo real (cuánto se gasta de un producto al mes, en qué
          clínica), y es la base para reconstruir el inventario de una fecha pasada partiendo de{" "}
          <a href="#get-bi-inventario" style={styles.link}>inventario</a>.
        </p>

        <H4>Query params</H4>
        <Table
          headers={["Param", "Tipo", "Descripción"]}
          rows={[
            ["desde", "string", "YYYY-MM-DD. Filtra por la fecha del movimiento. Formato inválido = filtro ignorado en silencio."],
            ["hasta", "string", "YYYY-MM-DD. Filtra por la fecha del movimiento."],
            ["cursor", "integer", "Keyset sobre id. Por defecto 0."],
            ["limit", "integer", "Por defecto 500, tope 1000."],
          ]}
        />

        <H4>Campos devueltos</H4>
        <Table
          headers={["Campo", "Tipo", "Descripción"]}
          rows={[
            ["id", "integer", "ID del movimiento. Es la CLAVE DE PAGINACIÓN."],
            ["cliente_id", "integer", "Clínica donde ocurrió el movimiento."],
            ["producto_id", "integer", "Producto afectado."],
            ["producto_nombre", "string", "Nombre actual del producto."],
            ["ref_proveedor", "string", "Referencia del proveedor."],
            ["tipo", "string", "entrada, salida, ajuste o set_inicial. Ver la tabla de abajo."],
            ["cantidad_anterior", "number", "Existencias ANTES del movimiento."],
            ["cantidad_nueva", "number", "Existencias DESPUÉS del movimiento."],
            ["delta", "number", "Diferencia aplicada. Negativa en las salidas."],
            ["metodo", "string", 'Origen del movimiento: "api" si entró por la API de Stock, u otro valor para web y app.'],
            ["fecha", "datetime", "Momento del movimiento. Es el campo sobre el que actúan desde y hasta."],
            ["pedido_id", "integer", "Pedido que originó el movimiento, o null. Permite unir consumo con compra."],
            ["lote_numero", "string", "Lote asociado, o null si el movimiento no tenía lote."],
            ["fecha_vencimiento", "date", "Vencimiento de ese lote, o null."],
          ]}
        />

        <H4>Tipos de movimiento</H4>
        <Table
          headers={["Tipo", "Qué es", "Cómo tratarlo en un informe"]}
          rows={[
            [
              "entrada",
              "Suma de unidades: recepción de mercancía o alta manual.",
              "Cuenta como aprovisionamiento. delta positivo.",
            ],
            [
              "salida",
              "Resta de unidades: consumo real.",
              "Es la base del análisis de gasto. delta negativo.",
            ],
            [
              "ajuste",
              "Corrección manual tras un recuento: mermas, roturas o errores previos.",
              "NO es consumo. Si lo mezclas con las salidas, inflarás el gasto con lo que en realidad son pérdidas o correcciones de datos. Analízalo aparte: un exceso de ajustes es un síntoma de mala calidad del inventario.",
            ],
            [
              "set_inicial",
              "Carga inicial al empezar a gestionar el stock de un producto.",
              "EXCLÚYELO de cualquier serie de consumo o de compra. Es un asiento de arranque, no una operación real, y suele tener un delta enorme que distorsiona cualquier media.",
            ],
          ]}
        />

        <Callout type="warning" icon={<AlertTriangle size={16} />}>
          <strong>Este dataset no tiene un campo{" "}
          <code style={styles.codeInline}>cantidad</code>, y es a propósito.</strong> Tiene{" "}
          <code style={styles.codeInline}>cantidad_anterior</code>,{" "}
          <code style={styles.codeInline}>cantidad_nueva</code> y{" "}
          <code style={styles.codeInline}>delta</code>. Para medir consumo usa{" "}
          <code style={styles.codeInline}>delta</code>, nunca{" "}
          <code style={styles.codeInline}>cantidad_nueva</code>: esta última es un{" "}
          <strong>saldo</strong>, y sumar saldos no significa absolutamente nada.
        </Callout>

        <H4>Ejemplos</H4>
        <CodeTabs
          tabs={getRequestTabs(
            "/api/v1/bi/movimientos",
            "?desde=2026-01-01&hasta=2026-06-30&limit=1000"
          )}
        />

        <H4>Respuesta 200</H4>
        <Pre>{`{
  "data": [
    {
      "id": 8821,
      "cliente_id": 42,
      "producto_id": 12345,
      "producto_nombre": "Jeringa 5ml",
      "ref_proveedor": "JER-5ML",
      "tipo": "salida",
      "cantidad_anterior": 140,
      "cantidad_nueva": 135,
      "delta": -5,
      "metodo": "api",
      "fecha": "2026-05-22T09:15:32.000Z",
      "pedido_id": null,
      "lote_numero": "L240115A",
      "fecha_vencimiento": "2026-12-31"
    }
  ],
  "count": 1,
  "next_cursor": null
}`}</Pre>

        <H4>Códigos</H4>
        <Table
          headers={["Status", "Cuándo"]}
          rows={[
            ["200", "Token válido y gestión de stock activa en el cliente del token."],
            ["401", "Token ausente, mal formado, revocado o inexistente."],
            ["403 stock_disabled", "El cliente dueño del token no tiene contratada la gestión de stock."],
            ["500 auth_error", "Fallo al validar el token."],
            ["500 internal_error", "Error al consultar."],
          ]}
        />
      </EndpointCard>
    </Section>
  );
}

function EndpointBiCatalogo() {
  return (
    <Section id="get-bi-catalogo" title="Catálogo (BI)">
      <EndpointCard
        method="GET"
        path="/api/v1/bi/catalogo"
        summary="Dimensión de producto: ficha con familia, subfamilia, marca, embalaje y tipo de IVA. Una fila por producto."
      >
        <Callout type="danger" icon={<Shield size={16} />}>
          <strong>Requiere el servicio de gestión de stock.</strong> Con{" "}
          <code style={styles.codeInline}>gestion_stock</code> inactiva en el cliente del token,
          devuelve <code style={styles.codeInline}>403 stock_disabled</code>.
        </Callout>

        <Callout type="warning" icon={<AlertTriangle size={16} />}>
          <strong>Solo incluye productos presentes en el stock de la red.</strong> No es el catálogo
          global de CentralSalud: si la red nunca ha tenido existencias de una referencia, esa
          referencia <strong>no aparece aquí</strong>. Consecuencia directa al modelar: un producto
          comprado hace años y ya retirado del stock puede figurar en{" "}
          <a href="#get-bi-ventas" style={styles.link}>ventas-lineas</a> y{" "}
          <strong>no tener fila en esta dimensión</strong>. Usa una relación que tolere huérfanos (un
          LEFT JOIN, no un INNER) o perderás líneas de venta sin darte cuenta.
        </Callout>

        <H4>Query params</H4>
        <Table
          headers={["Param", "Tipo", "Descripción"]}
          rows={[
            ["cursor", "integer", "Keyset sobre producto_id. Por defecto 0."],
            ["limit", "integer", "Por defecto 500, tope 1000."],
          ]}
        />
        <p style={styles.p}>
          Es una dimensión, no una serie temporal:{" "}
          <code style={styles.codeInline}>desde</code> y{" "}
          <code style={styles.codeInline}>hasta</code> <strong>se ignoran</strong> si los envías.
        </p>

        <H4>Campos devueltos</H4>
        <Table
          headers={["Campo", "Tipo", "Descripción"]}
          rows={[
            ["producto_id", "integer", "ID del producto. Es la CLAVE DE PAGINACIÓN y la clave de unión con ventas-lineas, inventario, lotes y movimientos."],
            ["nombre", "string", "Nombre ACTUAL del producto. Puede diferir del producto_nombre congelado en las líneas de pedido antiguas."],
            ["ref_proveedor", "string", "Referencia del proveedor."],
            ["unidadbasica", "string", "Texto descriptivo del embalaje."],
            ["cantidad_embalaje", "number", "Unidades que trae un embalaje."],
            ["familia", "string", "Familia de producto. Primer nivel de agrupación en los informes de gasto."],
            ["subfamilia", "string", "Segundo nivel de agrupación."],
            ["marca", "string", "Marca del producto."],
            ["tipo_iva", "string", "Tipo de IVA aplicable al producto."],
          ]}
        />
        <p style={styles.p}>
          <code style={styles.codeInline}>familia</code>,{" "}
          <code style={styles.codeInline}>subfamilia</code> y{" "}
          <code style={styles.codeInline}>marca</code> son los ejes por los que se corta el gasto en
          casi cualquier cuadro de mando de compras. Pueden venir{" "}
          <code style={styles.codeInline}>null</code> si el producto no está clasificado: agrúpalos
          bajo una etiqueta explícita del tipo "Sin clasificar" en lugar de dejarlos en blanco.
        </p>

        <H4>Ejemplos</H4>
        <CodeTabs tabs={getRequestTabs("/api/v1/bi/catalogo", "?limit=1000")} />

        <H4>Respuesta 200</H4>
        <Pre>{`{
  "data": [
    {
      "producto_id": 12345,
      "nombre": "Jeringa 5ml",
      "ref_proveedor": "JER-5ML",
      "unidadbasica": "1 caja de 100",
      "cantidad_embalaje": 100,
      "familia": "Desechables",
      "subfamilia": "Jeringas",
      "marca": "MedTech",
      "tipo_iva": "21"
    }
  ],
  "count": 1,
  "next_cursor": null
}`}</Pre>

        <H4>Códigos</H4>
        <Table
          headers={["Status", "Cuándo"]}
          rows={[
            ["200", "Token válido y gestión de stock activa en el cliente del token."],
            ["401", "Token ausente, mal formado, revocado o inexistente."],
            ["403 stock_disabled", "El cliente dueño del token no tiene contratada la gestión de stock."],
            ["500 auth_error", "Fallo al validar el token."],
            ["500 internal_error", "Error al consultar."],
          ]}
        />
      </EndpointCard>
    </Section>
  );
}

function EndpointBiClinicas() {
  return (
    <Section id="get-bi-clinicas" title="Clínicas">
      <EndpointCard
        method="GET"
        path="/api/v1/bi/clinicas"
        summary="Dimensión de centros: una fila por clínica de la red, con ubicación, jerarquía matriz-hija y servicios activos."
      >
        <p style={styles.p}>
          Es la dimensión que da nombre y geografía a todos los{" "}
          <code style={styles.codeInline}>cliente_id</code> que aparecen en los demás datasets. Sin
          ella, tus informes muestran identificadores numéricos en lugar de centros.
        </p>

        <H4>Query params</H4>
        <Table
          headers={["Param", "Tipo", "Descripción"]}
          rows={[
            ["cursor", "integer", "Keyset sobre cliente_id. Por defecto 0."],
            ["limit", "integer", "Por defecto 500, tope 1000."],
          ]}
        />
        <p style={styles.p}>
          Dimensión, no serie temporal: <code style={styles.codeInline}>desde</code> y{" "}
          <code style={styles.codeInline}>hasta</code> <strong>se ignoran</strong>. Suele caber en
          una sola página.
        </p>

        <H4>Campos devueltos</H4>
        <Table
          headers={["Campo", "Tipo", "Descripción"]}
          rows={[
            ["cliente_id", "integer", "ID de la clínica. Es la CLAVE DE PAGINACIÓN y la clave de unión con el resto de datasets."],
            ["razon_social", "string", "Denominación fiscal completa."],
            ["alias", "string", "Nombre corto de uso interno. Suele ser el que conviene mostrar en los informes."],
            ["localidad", "string", "Localidad del centro."],
            ["provincia", "string", "Provincia. Eje habitual para mapas y agrupaciones territoriales."],
            ["c_postal", "string", "Código postal. Es TEXTO, no número: conserva los ceros a la izquierda."],
            ["cif", "string", "CIF de la clínica."],
            ["clinica_matriz", "integer", "cliente_id de su matriz, o null si no cuelga de ninguna."],
            ["es_matriz", "boolean", "true si la clínica tiene hijas colgando."],
            ["gestion_stock", "string", "Modalidad del servicio de stock: inactivo, albaran, picking o mixto."],
            ["gestion_albaranes", "string", "Modalidad de gestión de albaranes del centro."],
            ["pedidos_pendientes", "number", "Pedidos pendientes en el momento de la consulta. Es un dato VIVO, no histórico."],
          ]}
        />

        <Callout type="info" icon={<Network size={16} />}>
          <strong><code style={styles.codeInline}>clinica_matriz</code> y{" "}
          <code style={styles.codeInline}>es_matriz</code> te dan la jerarquía de la red.</strong>{" "}
          Con ellos construyes el desglose por grupo sin cablearlo a mano: las filas con{" "}
          <code style={styles.codeInline}>clinica_matriz = null</code> son las raíces y el resto
          cuelga de ellas. Recuerda que solo verás las clínicas dentro del alcance de tu token.
        </Callout>

        <Callout type="warning" icon={<AlertTriangle size={16} />}>
          <strong><code style={styles.codeInline}>pedidos_pendientes</code> es un contador del
          instante de la consulta.</strong> Si lo cargas en un histórico junto a datos de hace un
          mes, estarás mezclando una foto de hoy con hechos del pasado y el informe mentirá.
          Trátalo como un indicador de estado en vivo, nunca como una métrica de serie temporal.
        </Callout>

        <H4>Ejemplos</H4>
        <CodeTabs tabs={getRequestTabs("/api/v1/bi/clinicas", "?limit=1000")} />

        <H4>Respuesta 200</H4>
        <Pre>{`{
  "data": [
    {
      "cliente_id": 42,
      "razon_social": "Clínica Dental Norte S.L.",
      "alias": "Dental Norte",
      "localidad": "Bilbao",
      "provincia": "Bizkaia",
      "c_postal": "48001",
      "cif": "B12345678",
      "clinica_matriz": 7,
      "es_matriz": false,
      "gestion_stock": "albaran",
      "gestion_albaranes": "activo",
      "pedidos_pendientes": 3
    }
  ],
  "count": 1,
  "next_cursor": null
}`}</Pre>

        <H4>Códigos</H4>
        <Table
          headers={["Status", "Cuándo"]}
          rows={[
            ["200", "Siempre que el token sea válido."],
            ["401", "Token ausente, mal formado, revocado o inexistente."],
            ["500 auth_error", "Fallo al validar el token."],
            ["500 internal_error", "Error al consultar."],
          ]}
        />
        <p style={styles.p}>
          Dataset <strong>abierto</strong>: no requiere el servicio de gestión de stock. De hecho es
          la vía para <strong>saber qué clínicas de tu red lo tienen contratado</strong>, leyendo el
          campo <code style={styles.codeInline}>gestion_stock</code> de cada fila.
        </p>
      </EndpointCard>
    </Section>
  );
}

function EndpointBiProveedores() {
  return (
    <Section id="get-bi-proveedores" title="Proveedores">
      <EndpointCard
        method="GET"
        path="/api/v1/bi/proveedores"
        summary="Dimensión de proveedores: una fila por proveedor con el que la red ha hecho algún pedido."
      >
        <H4>Query params</H4>
        <Table
          headers={["Param", "Tipo", "Descripción"]}
          rows={[
            ["cursor", "integer", "Keyset sobre proveedor_id. Por defecto 0."],
            ["limit", "integer", "Por defecto 500, tope 1000."],
          ]}
        />
        <p style={styles.p}>
          Dimensión, no serie temporal: <code style={styles.codeInline}>desde</code> y{" "}
          <code style={styles.codeInline}>hasta</code> <strong>se ignoran</strong>.
        </p>

        <H4>Campos devueltos</H4>
        <Table
          headers={["Campo", "Tipo", "Descripción"]}
          rows={[
            ["proveedor_id", "integer", "ID del proveedor. Es la CLAVE DE PAGINACIÓN y la clave de unión con ventas-lineas, pedidos y facturacion."],
            ["nombre", "string", "Nombre del proveedor."],
            ["fecha_registro", "datetime", "Alta del proveedor en la plataforma. Es la fecha de registro, NO la del primer pedido."],
          ]}
        />

        <Callout type="warning" icon={<AlertTriangle size={16} />}>
          <strong>Solo aparecen los proveedores con los que tu red ha pedido algo.</strong> No es el
          maestro completo de proveedores de CentralSalud: un proveedor dado de alta con el que
          todavía no habéis hecho ningún pedido <strong>no sale aquí</strong>. Por eso el listado
          crece con el tiempo, y por eso no sirve para responder "qué proveedores tengo
          disponibles", solo "con cuáles he trabajado".
        </Callout>

        <H4>Ejemplos</H4>
        <CodeTabs tabs={getRequestTabs("/api/v1/bi/proveedores", "?limit=1000")} />

        <H4>Respuesta 200</H4>
        <Pre>{`{
  "data": [
    {
      "proveedor_id": 17,
      "nombre": "Distribuciones Médicas SA",
      "fecha_registro": "2021-09-03T10:04:12.000Z"
    }
  ],
  "count": 1,
  "next_cursor": null
}`}</Pre>

        <H4>Códigos</H4>
        <Table
          headers={["Status", "Cuándo"]}
          rows={[
            ["200", "Siempre que el token sea válido."],
            ["401", "Token ausente, mal formado, revocado o inexistente."],
            ["500 auth_error", "Fallo al validar el token."],
            ["500 internal_error", "Error al consultar."],
          ]}
        />
        <p style={styles.p}>
          Dataset <strong>abierto</strong>: no requiere el servicio de gestión de stock.
        </p>
      </EndpointCard>
    </Section>
  );
}

function BiPowerQuery() {
  return (
    <Section id="bi-powerquery" title="Consumo desde Excel / Power BI">
      <p style={styles.p}>
        El caso de uso más habitual de los datasets BI es cargarlos en Excel o Power BI. Power Query
        no sabe paginar solo, así que hay que escribir el bucle en M. Abre{" "}
        <strong>Datos → Obtener datos → Consulta en blanco → Editor avanzado</strong> y pega esto:
      </p>

      <CodeTabs
        tabs={[
          {
            label: "M · descarga completa",
            code: `let
    Token = "csk_live_xxx...",          // mejor: guárdalo como parámetro de la consulta
    Base  = "${BASE_URL}",

    // Una llamada = una página. cursor 0 arranca desde el principio.
    Pagina = (cursor as number) as record =>
        Json.Document(
            Web.Contents(Base, [
                RelativePath = "api/v1/bi/ventas-lineas",
                Query = [
                    desde  = "2026-01-01",
                    hasta  = "2026-06-30",
                    limit  = "1000",
                    cursor = Text.From(cursor)
                ],
                Headers = [ Authorization = "Bearer " & Token ]
            ])
        ),

    // Bucle keyset: sigue mientras el cursor no sea null.
    Paginas = List.Generate(
        () => [ cursor = 0, r = Pagina(0) ],
        each [cursor] <> null,
        each [
            cursor = [r][next_cursor],
            r      = if [r][next_cursor] = null
                     then [ data = {}, next_cursor = null ]
                     else Pagina([r][next_cursor])
        ],
        each [r][data]
    ),

    Filas  = List.Combine(Paginas),
    Tabla  = Table.FromRecords(Filas),

    Tipada = Table.TransformColumnTypes(Tabla, {
        {"linea_id", Int64.Type}, {"pedido_id", Int64.Type},
        {"fecha_pedido", type date}, {"fecha_cierre", type date},
        {"estado", Int64.Type}, {"cliente_id", Int64.Type},
        {"cantidad", type number}, {"precio", type number},
        {"iva", type number}, {"total_linea", type number},
        {"total_linea_iva", type number}
    })
in
    Tipada`,
          },
          {
            label: "M · estados de pedido",
            code: `let
    Token = "csk_live_xxx...",
    Base  = "${BASE_URL}",

    Pagina = (cursor as number) as record =>
        Json.Document(
            Web.Contents(Base, [
                RelativePath = "api/v1/bi/estados-pedido",
                Query   = [ limit = "1000", cursor = Text.From(cursor) ],
                Headers = [ Authorization = "Bearer " & Token ]
            ])
        ),

    Paginas = List.Generate(
        () => [ cursor = 0, r = Pagina(0) ],
        each [cursor] <> null,
        each [
            cursor = [r][next_cursor],
            r      = if [r][next_cursor] = null
                     then [ data = {}, next_cursor = null ]
                     else Pagina([r][next_cursor])
        ],
        each [r][data]
    ),

    Tabla = Table.FromRecords(List.Combine(Paginas)),

    Tipada = Table.TransformColumnTypes(Tabla, {
        {"id", Int64.Type}, {"pedido_id", Int64.Type},
        {"cliente_id", Int64.Type},
        {"estado_anterior", Int64.Type}, {"estado_nuevo", Int64.Type},
        {"cambiado_en", type datetime}
    })
in
    Tipada`,
          },
          {
            label: "M · página única",
            code: `// Versión mínima para probar la conexión antes de montar el bucle.
let
    Token = "csk_live_xxx...",
    Base  = "${BASE_URL}",

    Origen = Json.Document(
        Web.Contents(Base, [
            RelativePath = "api/v1/bi/ventas-lineas",
            Query   = [ limit = "100" ],
            Headers = [ Authorization = "Bearer " & Token ]
        ])
    ),

    Tabla = Table.FromRecords(Origen[data])
in
    Tabla`,
          },
        ]}
      />

      <H3>Reglas de oro en Power Query</H3>
      <Table
        headers={["Regla", "Por qué"]}
        rows={[
          [
            "Usa RelativePath y Query, no concatenes la URL",
            "Web.Contents(Base, [RelativePath=..., Query=...]) mantiene una URL base estática, que es lo que Power BI Service necesita para validar la fuente en las actualizaciones programadas. Si concatenas la URL completa, la actualización en la nube falla aunque funcione en el escritorio.",
          ],
          [
            "Elige la credencial Anónimo",
            "El token va en la cabecera Authorization que pones tú a mano. Si eliges cualquier otro tipo de autenticación, Power Query genera su propia cabecera y pisa la tuya.",
          ],
          [
            "Los valores de Query van como texto",
            "Text.From(cursor) y limit = \"1000\". Un número suelto en el registro Query da error.",
          ],
          [
            "Guarda el token como parámetro, no incrustado",
            "Así lo rotas en un solo sitio y no viaja dentro del .pbix compartido.",
          ],
          [
            "Sube el limit a 1000",
            "Es el tope. Menos páginas, menos handshakes, actualización notablemente más rápida.",
          ],
        ]}
      />

      <Callout type="danger" icon={<AlertTriangle size={16} />}>
        <strong>Diagnóstico engañoso: "No hemos podido autenticarle con las credenciales
        proporcionadas".</strong> Power Query traduce{" "}
        <strong>cualquier 401 o 403</strong> a ese mensaje, sin distinguir la causa real. Antes de
        revisar la configuración de credenciales, comprueba el token con un{" "}
        <code style={styles.codeInline}>curl</code> directo:
      </Callout>
      <Pre>{`curl -i "${BASE_URL}/api/v1/bi/ventas-lineas?limit=1" \\
  -H "Authorization: Bearer csk_live_xxx..."`}</Pre>
      <Table
        headers={["Lo que devuelve curl", "Qué significa de verdad"]}
        rows={[
          ["200", "El token está bien: el problema es la configuración de la credencial en Power Query. Borra los permisos de origen de datos y vuelve a elegir Anónimo."],
          ["401 missing_token", "La cabecera no llegó. Revisa que Headers esté dentro del registro de opciones de Web.Contents."],
          ["401 invalid_token_format", "Al token le falta el prefijo csk_live_ o se coló un espacio al copiarlo."],
          ["401 invalid_token", "El token fue revocado o regenerado. Reclama el nuevo desde el panel API Keys."],
          ["403 stock_disabled", "El token es válido: lo que falta es el servicio de gestión de stock en el cliente dueño del token. Solo ocurre en inventario, lotes, movimientos y catalogo. No toques la configuración de credenciales, no es un problema de autenticación."],
          ["500 auth_error", "Problema temporal nuestro al validar el token. Reinténtalo antes de cambiar la configuración."],
        ]}
      />
      <p style={styles.p}>
        Para <strong>actualización programada en Power BI Service</strong>: la fuente que hay que
        configurar es <code style={styles.codeInline}>{BASE_URL}</code> con nivel de privacidad{" "}
        <em>Organizational</em> o <em>Public</em> y autenticación anónima. Si la configuras como{" "}
        <em>Private</em> mezclada con otras fuentes, el motor de privacidad puede bloquear la
        combinación de consultas.
      </p>
    </Section>
  );
}


// ─── Building blocks ───────────────────────────────────────────────────────
function Section({ id, title, children }) {
  return (
    <section id={id} style={styles.section}>
      <h2 style={styles.h2}>{title}</h2>
      {children}
    </section>
  );
}

function H3({ children }) { return <h3 style={styles.h3}>{children}</h3>; }
function H4({ children }) { return <h4 style={styles.h4}>{children}</h4>; }

function EndpointCard({ method, path, summary, children }) {
  return (
    <div style={styles.endpointCard}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <MethodTag method={method} />
        <span style={styles.endpointPath}>{path}</span>
        <span style={styles.authBadge}>
          <Key size={10} /> Token
        </span>
      </div>
      <p style={styles.endpointSummary}>{summary}</p>
      {children}
    </div>
  );
}

function MethodTag({ method, small = false }) {
  const colors = {
    GET: "#2563eb",
    POST: PRIMARY,
    PUT: "#f59e0b",
    DELETE: "#dc2626",
  };
  return (
    <span
      style={{
        ...styles.methodTag,
        backgroundColor: colors[method] || "#6b7280",
        fontSize: small ? 9 : 12,
        padding: small ? "2px 5px" : "4px 8px",
      }}
    >
      {method}
    </span>
  );
}

function Pre({ children }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(children);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {}
  };
  return (
    <div style={styles.preWrap}>
      <button onClick={handleCopy} style={styles.copyBtn} title="Copiar al portapapeles">
        {copied ? <Check size={12} /> : <Copy size={12} />}
      </button>
      <pre style={styles.pre}><code>{children}</code></pre>
    </div>
  );
}

function CodeTabs({ tabs }) {
  const [active, setActive] = useState(0);
  const [copied, setCopied] = useState(false);
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(tabs[active].code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {}
  };
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
        {tabs.map((t, i) => (
          <button
            key={i}
            onClick={() => setActive(i)}
            style={{
              cursor: "pointer",
              padding: "7px 14px",
              fontSize: 12,
              fontWeight: 600,
              border: "1px solid #e5e7eb",
              borderBottom: "none",
              borderRadius: "8px 8px 0 0",
              background: active === i ? "#0f172a" : "#f8fafc",
              color: active === i ? "#fff" : "#6b7280",
              borderColor: active === i ? "#0f172a" : "#e5e7eb",
            }}
          >
            <Code size={11} style={{ verticalAlign: "middle", marginRight: 4 }} />
            {t.label}
          </button>
        ))}
      </div>
      <div style={{ position: "relative" }}>
        <button onClick={handleCopy} style={{ ...styles.copyBtn, top: 8 }}>
          {copied ? <Check size={12} /> : <Copy size={12} />}
        </button>
        <pre style={{ ...styles.pre, borderTopLeftRadius: 0, marginTop: 0 }}>
          <code>{tabs[active].code}</code>
        </pre>
      </div>
    </div>
  );
}

function Callout({ type, icon, children }) {
  const palette = {
    info: { bg: PRIMARY_LIGHT, border: PRIMARY, color: "#065f46" },
    warning: { bg: "#fef3c7", border: "#fcd34d", color: "#92400e" },
    danger: { bg: "#fef2f2", border: "#fca5a5", color: "#991b1b" },
  }[type] || { bg: PRIMARY_LIGHT, border: PRIMARY, color: "#065f46" };
  return (
    <div
      style={{
        padding: "14px 18px",
        borderRadius: 10,
        margin: "16px 0",
        borderLeft: `4px solid ${palette.border}`,
        background: palette.bg,
        color: palette.color,
        fontSize: 13,
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
      }}
    >
      {icon && <span style={{ marginTop: 1 }}>{icon}</span>}
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
}

function Table({ headers, rows }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={styles.table}>
        <thead>
          <tr>{headers.map((h, i) => <th key={i} style={styles.th}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {row.map((cell, j) => (
                <td key={j} style={styles.td}>
                  {/* Marca "sí" / "no" en columna Req */}
                  {cell === "sí" ? <span style={{ color: "#dc2626", fontWeight: 700 }}>sí</span>
                    : cell === "no" ? <span style={{ color: "#9ca3af" }}>no</span>
                    : typeof cell === "string" && /^[a-z_]+$/.test(cell) ? <code style={styles.codeInline}>{cell}</code>
                    : cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─── Estilos ───────────────────────────────────────────────────────────────
const styles = {
  // Layout flex: sidebar fija al borde izquierdo, contenido ocupa el resto.
  // Sin maxWidth en .page para evitar que la barra "flote" en pantallas anchas.
  page: {
    background: "#f8fafc",
    minHeight: "100vh",
    color: "#1f2937",
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    lineHeight: 1.6,
    display: "flex",
    alignItems: "stretch",
  },
  sidebar: {
    width: 260,
    flexShrink: 0,
    position: "sticky",
    top: 0,
    alignSelf: "flex-start",
    height: "100vh",
    overflowY: "auto",
    background: "#fff",
    borderRight: "1px solid #e5e7eb",
    padding: "24px 16px",
  },
  // El contenido principal ocupa TODO el espacio sobrante de izquierda a
  // derecha; no se centra ni tiene maxWidth. Da la sensación de panel real
  // en pantallas anchas.
  mainContainer: { flex: 1, minWidth: 0 },
  main: { padding: "40px 56px", width: "100%" },
  brand: { display: "flex", alignItems: "center", gap: 10, marginBottom: 24 },
  brandIcon: {
    width: 36, height: 36, borderRadius: 10,
    background: PRIMARY_LIGHT,
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: 4,
  },
  navSection: {
    fontSize: 10, textTransform: "uppercase", fontWeight: 700, color: "#9ca3af",
    letterSpacing: 0.5, margin: "14px 0 6px", paddingLeft: 8,
  },
  navLink: {
    display: "flex", alignItems: "center",
    padding: "7px 10px", borderRadius: 6,
    color: "#1f2937", textDecoration: "none", fontSize: 13,
    transition: "all 0.15s",
  },
  navLinkActive: { background: PRIMARY_LIGHT, color: PRIMARY_DARK, fontWeight: 600 },
  h1: { fontSize: 32, fontWeight: 800, marginBottom: 8, letterSpacing: -0.5 },
  versionBadge: {
    display: "inline-block", verticalAlign: "middle",
    fontSize: 12, fontWeight: 700, padding: "4px 8px", borderRadius: 6,
    background: PRIMARY, color: "#fff", marginLeft: 10,
  },
  lead: { fontSize: 16, color: "#6b7280", maxWidth: 720 },
  section: { marginTop: 40 },
  h2: { fontSize: 22, fontWeight: 700, margin: "0 0 12px", paddingBottom: 8, borderBottom: `2px solid ${PRIMARY}` },
  h3: { fontSize: 17, fontWeight: 700, margin: "28px 0 8px" },
  h4: { fontSize: 12, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5, margin: "18px 0 6px" },
  p: { marginBottom: 12 },
  ul: { marginLeft: 24, marginBottom: 12, fontSize: 14 },
  endpointCard: {
    background: "#fff", border: "1px solid #e5e7eb", borderRadius: 12,
    padding: 22, margin: "16px 0", boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
  },
  methodTag: {
    fontFamily: "monospace", fontWeight: 700,
    borderRadius: 5, color: "#fff", letterSpacing: 0.5,
    display: "inline-block",
  },
  endpointPath: { fontFamily: "monospace", fontSize: 14, fontWeight: 600 },
  endpointSummary: { fontSize: 13, color: "#6b7280", marginTop: 4 },
  authBadge: {
    fontSize: 10, fontWeight: 700, padding: "3px 7px", borderRadius: 99,
    background: "#fef3c7", color: "#92400e", display: "inline-flex", alignItems: "center", gap: 3,
  },
  codeInline: { fontFamily: '"SF Mono", Menlo, Monaco, monospace', fontSize: 12, padding: "1px 5px", borderRadius: 3, background: "#f1f5f9", color: PRIMARY_DARK },
  preWrap: { position: "relative" },
  pre: {
    background: "#0f172a", color: "#e2e8f0",
    padding: "16px 20px", borderRadius: 10, overflowX: "auto",
    fontFamily: '"SF Mono", Menlo, Monaco, monospace', fontSize: 13, lineHeight: 1.55,
    margin: "12px 0", whiteSpace: "pre",
  },
  copyBtn: {
    position: "absolute", top: 20, right: 14, zIndex: 1,
    background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)",
    color: "#cbd5e0", borderRadius: 5, padding: "4px 8px", cursor: "pointer",
    display: "flex", alignItems: "center",
  },
  table: { width: "100%", borderCollapse: "collapse", margin: "12px 0" },
  th: { textAlign: "left", padding: "8px 10px", fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.5, background: "#f8fafc", borderBottom: "1px solid #e5e7eb" },
  td: { textAlign: "left", padding: "8px 10px", fontSize: 13, borderBottom: "1px solid #e5e7eb" },
  link: { color: PRIMARY_DARK, fontWeight: 600, textDecoration: "none" },
  footer: { marginTop: 80, paddingTop: 24, borderTop: "1px solid #e5e7eb", color: "#9ca3af", fontSize: 12 },
};
